/**
 * Career UI framing-text catalog (nav, buttons, auth, empty states — everything
 * not LLM-authored) (i18n-adr.md). English keys, English default text = the en
 * fallback; the `de-DE` overrides carry the German the user actually sees. ALL of
 * this framing text flows through `t()` — a hardcoded German literal in an element
 * or VM fails review (ADR I2 rule 2). Content prose (LLM-authored) is out of scope
 * here — it carries `PresentationMeta.locale` and renders `lang="de"`.
 */

import { createI18n, type I18n, type MessageValue } from "@platform/i18n"

/** English-default catalog — its keys are the typed surface of `t`. */
export const catalog = {
  "app.name": "Career",
  "app.tagline": "Find jobs that fit you. Generate applications that sound like you.",

  "nav.onboarding": "Onboarding",
  "nav.review": "Proposals",
  "nav.workshops": "Workshops",
  "nav.kompass": "Compass",
  "nav.suche": "Search",
  "nav.bewerbungen": "Applications",
  "nav.laeufe": "Runs",
  "nav.prozesse": "Processes",
  "nav.einstellungen": "AI settings",
  "nav.design": "Design & theme",
  "nav.herzschlag": "Heartbeat",
  "nav.mehr": "More",
  "nav.profil": "Profile",
  "nav.lebenslauf": "CV",
  "nav.belege": "Evidence",
  "nav.nachweise": "Documents",
  "a11y.skipToContent": "Skip to content",

  "auth.login": "Sign in",
  "auth.logout": "Sign out",
  "auth.devLogin": "Dev sign-in",
  "auth.signedInAs": "Signed in as {name}",

  // ─── App-bar chrome (theme toggle + resource metadata) ────────
  "theme.label": "Color theme",
  "theme.light": "Light",
  "theme.dark": "Dark",
  "theme.auto": "Auto",
  "bar.nav.label": "Sections",
  "bar.resourcePath.label": "Resource path",
  "meta.open": "View details",
  "meta.title": "This view",
  "meta.resource": "View",
  "meta.owner": "Owner",
  "meta.visibility": "Visibility",
  "meta.sharing": "Shared with",
  "meta.visibility.private": "Private",
  "meta.sharing.none": "Only you",

  // ─── Top navbar: breadcrumb + user menu (§7a) ─────────────────
  "breadcrumb.label": "You are here",
  "breadcrumb.switchStage": "Switch area",
  "breadcrumb.switchSection": "Switch section",
  "breadcrumb.appSpace": "Switch area",
  "breadcrumb.step": "Jump to step",
  "user.menu.open": "Account menu",
  "user.menu.title": "Account",
  // Workspace = the user's organizing period (deferred feature — a default label for now).
  "workspace.default": "Standard workspace",
  // Workshop display names for the Sub-area crumb (mirror the server method-pack names).
  "breadcrumb.workshop.self-knowledge": "Workshop 1 · Self-image",
  "breadcrumb.workshop.search-profile": "Workshop 2 · Search profile",
  "breadcrumb.workshop.profiler": "Workshop 3 · Profiler",

  "login.title": "Sign in to Career",
  "login.body": "Career helps you find jobs that fit you \u2014 and write applications that sound like you. Sign in with your platform account.",
  "login.devHint": "Development mode: test session active (without Keycloak).",
  "login.cta": "Sign in with Platform account",
  "brand.name": "Career Studio",
  "nav.collapseSidebar": "Collapse sidebar",
  "nav.expandSidebar": "Expand sidebar",
  "aside.toggle": "Toggle details panel",
  "aside.title": "Details",
  "aside.empty": "Contextual details for the current view will appear here.",

  // ─── Left pane: within-space section nav (§7) ─────────────────
  "sectionnav.label": "Sections",
  "sectionnav.empty": "No sections in this area yet.",
  "sectionnav.overview": "Overview",
  "sectionnav.ws.self": "Self-image",
  "sectionnav.ws.search": "Search profile",
  // S4 (naming half): »Profiler« named a tool, not a decision. The surface holds the weights
  // the match pass actually grades against, so the label now names the instrument — and pairs
  // cleanly with »Suchprofil«: what I look for, and how I decide.
  "sectionnav.ws.profiler": "Decision profile",
  // W4 »Schreibstil« — unlocks off W1, not off the W2→W3 chain (PREREQ.writing_style = self_knowledge).
  "sectionnav.ws.writing": "Writing style",
  // S567: `/compass` IS the document — not an »Overview« of links to it.
  "sectionnav.kompass.doc": "My compass",
  "sectionnav.onboarding.upload": "Documents",
  "sectionnav.indicator.open": "{n} open",
  // Each badge NAMES what it counts, so two badges in one pane never read as one quantity
  // disagreeing with itself: the CV badge counts every open decision on the CV, the Belege
  // badge the narrower subset of statements that still lack evidence.
  "sectionnav.indicator.cvDecisions": {
    one: "{n} open decision on your CV",
    other: "{n} open decisions on your CV",
  } as MessageValue,
  "sectionnav.indicator.evidenceGaps": {
    one: "{n} statement still without evidence",
    other: "{n} statements still without evidence",
  } as MessageValue,
  // Contextual left-pane state (plan-rail): the current area's stages/resources with state.
  "sectionnav.state.done": "done",
  "sectionnav.state.current": "current",
  "sectionnav.state.locked": "locked",
  "sectionnav.state.open": "open",
  "sectionnav.state.attention": "needs you",
  "sectionnav.locked.afterCardgame": "Finish the card game first",
  "sectionnav.locked.afterInterview": "Finish the interview first",
  "sectionnav.locked.afterSektion": "Finish the values section first",
  "sectionnav.locked.afterSearchProfile": "Finish the search profile first",
  "sectionnav.detail.round": "Round {round} of {total}",
  "sectionnav.stale.badge": "Outdated",
  "sectionnav.stale.hint": "Old view — redesign to follow",
  "sectionnav.account": "Account",

  // ─── Right pane: Platform AI Agent ────────────────────────────
  "ai.panel.title": "Platform AI",
  "ai.panel.subtitle": "Help with everything you do on the platform.",
  "ai.panel.toggle": "Toggle the Platform AI agent",
  "ai.panel.short": "AI",
  "ai.panel.apps": "Your apps",
  "ai.panel.thisApp": "This app",
  "ai.panel.moreSoon": "More applications appear here as you gain access to them.",
  "ai.panel.composerPlaceholder": "Ask Platform AI …",
  "ai.panel.comingSoon": "Coming soon",

  "notfound.title": "Page not found",
  "notfound.body": "This route does not exist.",
  "notfound.back": "Back to search",

  "common.comingSoon": "This view is being built in a later phase. The route, shell, and data plane already work.",
  "gated.hint": "Available with the appropriate authorization.",

  "surface.onboarding.title": "Onboarding",
  "surface.extraction.title": "Review proposals",
  "surface.workshops.title": "Workshops",
  "surface.workshopRunner.title": "Workshop",
  "surface.kompass.title": "Compass",
  "surface.werte.title": "My values",
  "surface.profil.title": "Profile",
  "surface.lebenslauf.title": "CV",
  "surface.belege.title": "Evidence ledger",
  "surface.nachweise.title": "Documents",
  "surface.suche.title": "Search",
  "surface.opportunity.title": "Opportunity",
  "surface.bewerbungen.title": "Applications",
  "surface.laeufe.title": "Runs",
  "surface.einstellungen.title": "AI settings",
  "surface.design.title": "Design & theme",
  "surface.grading.title": "Bench grading",
  "surface.benchLab.title": "Bench lab",

  "heartbeat.title": "Push-plane heartbeat",
  "heartbeat.intro": "Triggers a scripted search run through the real api and store, and renders it live from the push plane.",
  "heartbeat.trigger": "Trigger heartbeat",
  "heartbeat.triggering": "Running …",
  "heartbeat.railTitle": "Search runs",
  "heartbeat.empty": "No runs yet. Trigger a heartbeat to see the live rail.",
  "heartbeat.stage.discovering": "Discovering",
  "heartbeat.stage.verifying": "Verifying",
  "heartbeat.stage.matching": "Matching",
  "heartbeat.stage.enriching": "Enriching",
  "heartbeat.stage.done": "Done",
  "heartbeat.status.running": "running",
  "heartbeat.status.completed": "completed",
  "heartbeat.status.stopped": "stopped",
  "heartbeat.status.failed": "failed",
  "heartbeat.count.discovered": "{n} discovered",
  "heartbeat.count.verified": "{n} verified",
  "heartbeat.count.matched": "{n} matched",
  "heartbeat.error": "Error: {message}",

  // ─── Onboarding ───────────────────────────────────────────────
  "onboarding.title": "Onboarding",
  "onboarding.lead": "Upload your documents. We recognise your strengths in them — you decide what counts.",
  "onboarding.kind.cv": "CV",
  "onboarding.kind.letter": "Cover letter",
  "onboarding.kind.reference": "Reference",
  "onboarding.kind.certificate": "Certificate",
  "onboarding.kind.portrait": "Photo",
  "onboarding.formatHint": "We read PDF, DOCX, Markdown (.md) and text (.txt).",
  "onboarding.upload": "Upload",
  "onboarding.uploading": "Uploading …",
  "onboarding.uploadedN": { one: "{n} file uploaded", other: "{n} files uploaded" },
  "onboarding.error": "Error: {message}",
  "onboarding.start": "Start analysis",
  "onboarding.starting": "Starting …",
  "onboarding.runLabel": "Analysis",
  "onboarding.status.pending": "Ready",
  "onboarding.status.extracting": "Analysing …",
  "onboarding.status.proposed": "Proposals ready",
  "onboarding.status.confirmed": "Confirmed",
  "onboarding.status.failed": "Failed",
  "onboarding.count.docs": { one: "{n} document", other: "{n} documents" },
  "onboarding.count.claims": { one: "{n} statement", other: "{n} statements" },
  "onboarding.runEmpty": "No analysis started yet.",
  "onboarding.zeroClaims": "We couldn't find any readable text. Scanned documents or image files often have no text layer — please upload a PDF with selectable text if you can.",
  "onboarding.toReview": "Review proposals",

  // ─── Extraction review ────────────────────────────────────────
  "review.title": "Review proposals",
  "review.lead": "Review what we read from your documents. Nothing counts as evidence until you confirm it.",
  "review.error": "Error: {message}",
  "review.grade.evidenced": "evidenced",
  "review.grade.selfPositioning": "self-positioning",
  "review.grade.gap": "gap",
  "review.sources": { one: "{n} source", other: "{n} sources" },
  "review.confirm": "Confirm",
  "review.confirmed": "Confirmed",
  "review.edit": "Edit",
  "review.save": "Save",
  "review.discard": "Discard",
  "review.editLabel": "Edit statement",
  "review.claims.title": "Statements",
  "review.claims.count": "{open} open · {confirmed} confirmed",
  "review.claims.empty": "No statements yet. Start the analysis in onboarding first.",
  "review.claims.emptyAfterRun": "We couldn't find any readable text. Scanned documents or image files often have no text layer — please upload a PDF with selectable text if you can.",
  "review.confirmIngest": "Finish review",
  "review.rederive": "Re-analyse",
  "review.rederiving": "Re-analysing …",
  "review.cv.title": "CV draft",
  "review.cv.name": "Name",
  "review.cv.stations": "Positions",
  "review.cv.stationsN": { one: "{n} position", other: "{n} positions" },
  "review.cv.skills": "Skills",
  "review.cv.skillsN": { one: "{n} skill", other: "{n} skills" },
  "review.cv.empty": "No CV draft yet.",
  "review.cv.toProposal": "Review the CV proposal",
  // ─── CV onboarding proposal (propose → approve) ───────────────
  "cvProposal.eyebrow": "Onboarding",
  "cvProposal.title": "Your CV from your documents",
  "cvProposal.lead":
    "We read your uploaded documents and drafted a CV from them. Nothing is adopted until you say so — uncheck anything that isn't right.",
  "cvProposal.meta": "Selected to adopt: {selected} of {total} suggestions · read on {date}",
  "cvProposal.empty": "No proposal open. Upload your documents in onboarding and start the analysis.",
  "cvProposal.emptyCta": "To onboarding",
  "cvProposal.approve": "Adopt {n} entries",
  "cvProposal.approveOne": "Adopt one entry",
  "cvProposal.approving": "Adopting …",
  "cvProposal.selectAll": "Select all",
  "cvProposal.selectNone": "Deselect all",
  "cvProposal.required": "stays — your name carries the CV",
  "cvProposal.source": "from your {kind}",
  "cvProposal.sourceQuote": "The line we read it from:",
  "cvProposal.derived": "added by the model — no matching line in your documents",
  "cvProposal.doc.cv": "CV",
  "cvProposal.doc.letter": "cover letter",
  "cvProposal.doc.reference": "reference",
  "cvProposal.doc.certificate": "certificate",
  "cvProposal.doc.other": "document",
  "cvProposal.section.basics": "About you",
  "cvProposal.section.work": "Positions",
  "cvProposal.section.education": "Education",
  "cvProposal.section.projects": "Projects",
  "cvProposal.section.skills": "Skills",
  "cvProposal.section.languages": "Languages",
  "cvProposal.section.certificates": "Certificates",
  "cvProposal.section.awards": "Awards",
  "cvProposal.section.publications": "Publications",
  "cvProposal.section.volunteer": "Volunteering",
  "cvProposal.section.references": "References",
  "cvProposal.section.interests": "Interests",
  "cvProposal.done.title": "Adopted — this is your CV now",
  "cvProposal.done.body": "Version {version} is saved. {accepted} adopted, {rejected} discarded.",
  "cvProposal.done.toCv": "To the CV",
  "cvProposal.error": "Something went wrong: {message}",

  "review.profile.title": "Profile seed",
  "review.profile.noThroughline": "No throughline yet.",
  "review.profile.motives": { one: "{m} motivation", other: "{m} motivations" },
  "review.profile.values": { one: "{v} value", other: "{v} values" },
  "review.profile.empty": "No profile seed yet.",
  "review.narrative.title": "Candidate profile",
  "review.narrative.provenance": { one: "{n} cited passage", other: "{n} cited passages" },
  "review.narrative.authoredOn": "Created on {date}",
  "review.narrative.stale": "Out of date — confirmed evidence has changed",
  "review.narrative.author": "Create candidate profile",
  "review.narrative.reauthor": "Re-create",
  "review.narrative.authoring": "Creating …",
  "review.narrative.empty": "Confirm evidence, and your candidate profile — a profile that sounds like you — appears here.",

  // ─── Compass hub ──────────────────────────────────────────────
  "compass.lead": "Everything at a glance: who you are, what you can do, and how you prove it.",
  "compass.thread": "From the compass into your search, and from the search into your applications.",
  // Kompass hub hero tiles (title per tile; descriptions reuse the compass.* copy below).
  "compass.werte.title": "My values",
  "compass.werte.desc": "Your committed value pyramid and the four statements that place you.",
  "compass.profil.title": "Profile",
  "compass.lebenslauf.title": "CV",
  "compass.evidenzen.title": "Evidence",
  "compass.unterlagen.title": "Documents",
  // Kompass »Meine Werte« read-only view.
  "werte.view.kicker": "The result of Workshop 1",
  "werte.view.lead": "What you committed to: your value pyramid and the four statements that place you.",
  "werte.view.pyramidTitle": "Your value pyramid",
  "werte.view.wheelTitle": "Your value picture",
  "werte.view.statementsTitle": "Your statements",
  "werte.view.stmtGap": "Nothing here yet — sharpen this in the values section.",
  "werte.view.empty": "No values committed yet. Play the card game in Workshop ① and confirm your values section.",
  "werte.view.toWorkshop": "To Workshop ①",
  "werte.view.canonLink": "Open in the Self-image workshop →",
  "compass.profile.desc": "Your candidate profile and what drives you.",
  "compass.profile.ready": "ready",
  "compass.profile.pending": "not yet created",
  "compass.cv.desc": "Your CV — draft, versions, export, print.",
  "compass.cv.versions": { one: "{n} version", other: "{n} versions" },
  "compass.cv.draft": "draft open",
  "compass.evidence.desc": "Every graded statement you can back up.",
  "compass.evidence.confirmed": "{n} confirmed",
  "compass.evidence.open": "{n} open",
  "compass.assets.desc": "Your certificates, references and documents.",
  "compass.assets.count": { one: "{n} document", other: "{n} documents" },

  // ─── Area hubs (ruling D2 · app/lib/hub.ts) — editorial hero + state rows ──────
  "compass.hub.eyebrow": "Your compass",
  "compass.hub.title.empty": "Build a clear picture of yourself.",
  "compass.hub.title.ready": "Your compass is set.",
  "compass.hub.lead.empty": "Everything you'll draw on later starts here. Begin with your values.",
  "compass.hub.lead.ready": "Your durable self-view — take it into your search.",
  "compass.hub.cta.werte": "To the values section",
  "compass.hub.cta.search": "On to your search",
  "compass.hub.chip.open": "open",
  "compass.hub.chip.confirmed": "confirmed",
  "compass.hub.chip.ready": "done",

  // The hub ROUTES (operator ruling 2026-07-30). The eyebrow names the AREA, not W1 — this
  // page is the door to all four workshops, not a page about the first one.
  "workshops.hub.eyebrow": "Your workshops",
  "workshops.hub.title.empty": "Get to know yourself first.",
  "workshops.hub.title.mid": "Keep going — you're in the middle of it.",
  "workshops.hub.title.done": "You've captured your values.",
  "workshops.hub.lead.empty": "Start with the card game — it turns a gut feeling into words you can build on.",
  "workshops.hub.lead.mid": "Pick up where you left off.",
  "workshops.hub.lead.done": "Your values are set. Take them into your compass.",
  "workshops.hub.cta.start": "To the card game",
  "workshops.hub.cta.resume": "Continue",
  "workshops.hub.cta.compass": "To your compass",
  // ONE headline per workshop smart link — what the person GETS, in a single line. The former
  // `workshops.overview.*` process pairs (»What you do« / »What it gives you«) and the
  // `workshops.hub.open` row button were DELETED with the S3 process overview: the hub routes,
  // and a workshop explains itself once you are inside it.
  "workshops.link.selfKnowledge": "Find out what actually drives you.",
  "workshops.link.searchProfile": "Pin down which roles you're really looking for.",
  "workshops.link.profiler": "Decide what a job has to get right for you.",
  "workshops.link.writingStyle": "Capture your voice so your letters sound like you.",

  "search.hub.eyebrow": "Your search",
  "search.hub.title.ready": "Find jobs that fit you.",
  "search.hub.title.notReady": "Sharpen your profile first.",
  "search.hub.lead.ready": "We search, verify and grade — you decide what's worth pursuing.",
  "search.hub.lead.notReady": "Confirm your values section — we search from it.",
  "search.hub.cta.sharpen": "Sharpen your profile first",
  "search.hub.meta.results": { one: "{n} job found so far", other: "{n} jobs found so far" },
  "search.hub.meta.none": "No search run yet.",

  "applications.hub.eyebrow": "Your applications",
  "applications.hub.title.empty": "No applications yet.",
  "applications.hub.title.active": { one: "{n} application in progress", other: "{n} applications in progress" },
  "applications.hub.lead.empty": "Find a job that fits and we'll turn it into an application that sounds like you.",
  "applications.hub.lead.active": "Where each one stands — from first draft to sent.",
  "applications.hub.cta.search": "To your search",
  "applications.hub.cta.open": "Open application",

  // ─── W2 »Was ich suche« + W3 »Wonach ich entscheide« (W4 workshop redesign) ───
  "sp.eyebrow": "Workshop 2",
  "sp.title": "What I'm looking for",
  "sp.lead": "Your search brief. Every field here changes what the search finds.",
  "sp.loading": "Loading your search profile…",
  "sp.state.ready": "Ready — your search can run.",
  "sp.state.blocked": "Not yet ready: without a target role the search cannot find anything.",
  "sp.add": "Add",
  "sp.remove": "Remove",
  "sp.proposal.lead": "From your CV:",
  "sp.proposal.accept": "Take these",
  "sp.roles.title": "Target roles",
  "sp.roles.hint": "Concrete job titles. Without at least one, the search cannot run.",
  "sp.roles.placeholder": "e.g. Lead Engineer GenAI",
  "sp.level.title": "Level",
  "sp.level.hint": "Pick the levels you're actually aiming at.",
  "sp.where.title": "Where",
  "sp.where.hint": "Places, and whether fully remote counts.",
  "sp.where.placeholder": "e.g. Nuremberg",
  "sp.exclude.title": "What should never show up",
  "sp.exclude.hint": "Postings with one of these in the TITLE or COMPANY NAME are dropped immediately. The posting text itself is not searched.",
  "sp.exclude.placeholder": "e.g. Temporary work",
  "sp.matters.title": "What matters to you",
  "sp.matters.hint": "Two sentences in your own words — they steer how postings are judged.",
  "sp.matters.save": "Save",
  "sp.matters.derived": "We're already searching for:",
  "sp.tuning.title": "Fine tuning",
  "sp.tuning.hint": "Sensible defaults — change them only if you want to.",
  "sp.tuning.minTier": "Quality floor",
  "sp.tuning.limit": "How many results",
  "sp.tuning.openWeb": "Company career pages",
  "sp.tuning.openWeb.on": "Included",
  "sp.tuning.openWeb.off": "Job boards only",
  "sp.tier.none": "Show everything",
  "sp.tier.weak": "Weak and up",
  "sp.tier.viable": "Viable and up",
  "sp.tier.strong": "Strong only",
  "sp.preview.title": "This is what the search will actually ask",
  "sp.preview.frame": "These are the exact queries the search will fire — built from your profile, not taken over literally.",
  "sp.preview.empty": "Add a target role and the queries your search will fire appear here.",
  "sp.preview.count": "{n} queries from your profile:",
  "sp.preview.more": "…and {n} more.",
  // S567 / P-A: the rubric is authored in Workshop 3 and RENDERED here, where it is consumed.
  "sp.criteria.title": "And this is what a find is measured against",
  "sp.criteria.frame":
    "Your search profile says what to look for — these weights say how much a find counts.",
  "sp.criteria.antiValues": "Your red lines:",
  "sp.criteria.edit": "Change your decision profile",

  // ─── W2 derive-first (W4): the tool proposes roles, the person disposes ───────
  "sp.derive.title": "Let us derive your search profile",
  "sp.derive.lead":
    "From your values, your statements and your CV we propose roles — including ones you hadn't thought of. You take what fits.",
  "sp.derive.intent.placeholder": "Anything you already know you want? (optional)",
  "sp.derive.cta": "Derive a search profile from my values & documents",
  "sp.derive.note": "This reads your workshop results and takes a moment.",
  "sp.gate.title": "First, Workshop 1",
  "sp.gate.lead":
    "Your search profile is derived from your values — so play the card game and shape your Values Section first.",
  "sp.gate.cta": "Go to Workshop 1",
  "sp.derive.pending": "Reading your values, your statements and your CV, and proposing roles that fit you…",
  "sp.derive.again": "Derive again",
  "sp.review.badge": "Proposal",
  "sp.review.note": "Nothing is saved yet — review it, adjust it, then take what fits.",
  "sp.review.rolesTitle": "Roles that fit you",
  "sp.review.accept": "Take this profile",
  "sp.review.rederive": "Derive again",
  "sp.review.notLoaded": "Your saved profile could not be loaded, so this proposal cannot be applied yet. Reload the page — the proposal is kept.",
  "sp.review.seniority": "Level",
  "sp.review.locations": "Where",
  "sp.review.exclude": "Excluded",
  "sp.saved.title": "Your derived search profile",
  "sp.refine.open": "Not quite right? Tell us what's missing",
  "sp.refine.placeholder": "e.g. more towards platform leadership, less pure engineering",
  "sp.refine.submit": "Derive again with your note",
  "sp.refine.submitting": "Deriving…",
  "sp.refine.cancel": "Cancel",
  "sp.refine.proposalLabel": "Suggestion",
  "sp.refine.accept": "Take it",
  "sp.refine.again": "Again",
  "sp.refine.discard": "Discard",
  "sp.advanced.title": "Adjust the fine detail",
  "sp.advanced.hint": "You can also edit every field directly.",

  "dp.eyebrow": "Workshop 3",
  "dp.title": "How I decide",
  "dp.lead": "What counts, and where your red lines are. Both re-sort your results.",
  "dp.loading": "Loading your decision profile…",
  "dp.gate.title": "First, your search profile",
  "dp.gate.lead":
    "Your decision profile weights what your search finds — so set up your search profile first.",
  "dp.gate.cta": "Go to my search profile",
  "dp.weights.title": "What weighs most",
  "dp.weights.hint": "Every point you give one dimension is a point another does not get.",
  "dp.weights.more": "More",
  "dp.weights.less": "Less",
  "dp.redlines.title": "Red lines",
  "dp.redlines.hint": "A posting that hits one of these drops a tier or two.",
  "dp.redlines.placeholder": "e.g. no room for life outside work",
  "dp.example.title": "What that does to a match",
  "dp.example.lead": "A sample role, scored with your weights right now:",
  "dp.example.score": "Score {score} — {tier}",
  "dp.example.penalty": "With a red line hit: {score} — {tier}",
  "dp.tier.strong": "strong match",
  "dp.tier.viable": "viable",
  "dp.tier.weak": "weak",
  "dp.tier.unsuited": "unsuited",
  "dp.state.authored": "Saved — your ranking uses these weights.",
  "dp.state.default": "Not set yet — everything currently weighs the same.",

  // ─── Golden path — the typed »next step« every workflow surface ends with (D4) ─
  "next.eyebrow": "Next step",
  "next.toSearchProfile": "On to what you're looking for",
  "next.toDecisionProfile": "On to how you decide",
  "next.locked.searchProfile": "Add at least one target role first.",
  "next.toCardgame": "To the values card game",
  "next.toInterview": "On to the values interview",
  "next.toSektion": "On to your values section",
  "next.toKompass": "On to your compass",
  "next.toProfil": "On to your profile",
  "next.toLebenslauf": "On to your CV",
  "next.toSuche": "On to your search",
  "next.provideMaterial": "Provide your material",
  "next.toBewerbungen": "To your applications",
  "next.locked.cardgame": "Confirm your values pyramid first.",
  "next.locked.interview": "Finish the values interview first.",
  "next.locked.sektion": "Confirm your values section first.",
  "next.locked.werte": "Confirm your values section first.",
  "next.locked.profile": "Author your profile first.",
  "next.locked.cv": "Create your CV first.",

  // ─── Profile ──────────────────────────────────────────────────
  "profile.lead": "Everything you've worked out about yourself, in one place: your why, your values, where you're heading — and how you come across.",
  "profile.empty": "Create your profile: start with onboarding and make your strengths visible with evidence.",
  "profile.identity.title": "Your throughline",
  "profile.narrative.title": "Candidate profile",
  "profile.narrative.degraded": "This is a preliminary version. For your fully written profile, create it once more.",
  "profile.facet.sources": { one: "{n} source", other: "{n} sources" },
  "profile.facets.motivations": "Motivations",
  "profile.facets.drives": "Drives",
  "profile.facets.values": "Values",
  "profile.facets.antiValues": "What doesn't fit you",
  "profile.facets.dealBreakers": "Deal-breakers",
  "profile.constraints.title": "Framework conditions",
  "profile.constraints.location": "Location",
  "profile.constraints.salary": "Salary",
  "profile.constraints.workModes": "Work model",

  // ─── Evidence ledger ──────────────────────────────────────────
  "ledger.lead": "We grade every statement we find in your documents. It counts as evidence only once you confirm it.",
  "ledger.filters": "Filter",
  "ledger.filter.all": "All",
  "ledger.status.proposed": "Open",
  "ledger.status.confirmed": "Confirmed",
  "ledger.count.evidenced": "{n} evidenced",
  "ledger.count.selfPositioning": "{n} self-positioning",
  "ledger.count.gap": { one: "{n} gap", other: "{n} gaps" },
  "ledger.count.confirmed": "{n} confirmed",
  "ledger.empty": "No statements yet. Start the analysis in onboarding first.",
  "ledger.emptyFiltered": "No statements match this filter.",

  // ─── CV workbench ─────────────────────────────────────────────
  "cv.lead": "Finalize your CV when you're happy with it. Only finalized versions can be exported.",
  "cv.empty": "No CV yet. Start with onboarding.",
  "cv.current.title": "Current version",
  "cv.draft.title": "Draft",
  "cv.draft.notExportable": "Draft — finalize before exporting",
  "cv.fixVersion": "Finalize version",
  "cv.fixing": "Finalizing …",
  "cv.portrait.add": "Add photo",
  "cv.portrait.replace": "Replace photo",
  "cv.entries.title": "Stations & evidence",
  "cv.entries.claims": { one: "{n} linked statement", other: "{n} linked statements" },
  "cv.entries.noClaims": "no evidence yet",
  "cv.versions.title": "Versions",
  "cv.versions.empty": "No finalized version yet. Finalize the draft to export or print it.",
  "cv.versions.label": "Version {n}",
  "cv.export.md": "Markdown",
  "cv.export.jsonld": "JSON-LD",
  "cv.print": "Print / PDF",
  "cv.print.action": "Print / save as PDF",
  "cv.print.back": "Back to CV",
  "cv.print.loading": "Loading …",
  "cv.print.notFound": "This version does not exist.",
  "cv.doc.eyebrow": "Compass · CV",
  "cv.doc.version": "Version {n} — finalized",
  "cv.belege.count": { one: "{n} evidence link", other: "{n} evidence links" },
  "cv.belege.openN": "{n} open",
  "cv.belege.toLedger": "To the evidence ledger",

  // ─── CV-Werkbank: provide material → analyse → resolve, all at the CV ──────
  "werkbank.empty.lead": "No CV yet. Provide your existing CV and reference material — the AI reads it in, you decide what counts.",
  "werkbank.provide.eyebrow": "Provide material",
  "werkbank.provide.title": "Start with your existing CV",
  "werkbank.provide.titleMore": "Add material",
  "werkbank.provide.lead": "Upload your CV, references and certificates. The AI reads them into your model — every entry stays yours to confirm or discard.",
  "werkbank.provide.start": "Start analysis",
  "werkbank.material.pick": "Choose file",
  "werkbank.resolve.eyebrow": "Read from your documents",
  "werkbank.resolve.title": "Review what the AI recognised",
  "werkbank.resolve.lead": "Take out anything that doesn't belong — the rest becomes your CV. Nothing is saved until you adopt it.",
  "werkbank.nachweise.eyebrow": "Your documents",
  "werkbank.nachweise.title": "Confirm your evidence",
  "werkbank.nachweise.lead": "References and certificates from your uploads — confirm the ones you want to attach to applications.",
  "werkbank.nachweise.uploaded": "Uploaded document ({kind})",
  "werkbank.nachweise.toLibrary": "To the documents library",
  "werkbank.needsYou.badge": "needs you",
  "werkbank.needsYou.section": "This section has open items awaiting your confirmation.",
  "werkbank.needsYou.count": { one: "{n} awaiting you", other: "{n} awaiting you" },

  // ─── Assets inventory ─────────────────────────────────────────
  "asset.lead": "Your certificates, references and portfolio — evidence for your applications.",
  "asset.empty": "No documents yet. Add one, or promote an uploaded document below.",
  "asset.kind.certificate": "Certificate",
  "asset.kind.workCertificate": "Reference letter",
  "asset.kind.reference": "Reference",
  "asset.kind.diploma": "Diploma",
  "asset.kind.portfolio": "Portfolio",
  "asset.kind.portrait": "Photo",
  "asset.kind.other": "Other",
  "asset.confirm": "Confirm",
  "asset.confirmed": "Confirmed",
  "asset.edit": "Edit",
  "asset.save": "Save",
  "asset.remove": "Remove",
  "asset.editTitle": "Edit title",
  "asset.editIssuer": "Edit issuer",
  "asset.issuer": "Issuer",
  "asset.add.group": "Add document",
  "asset.add.kindLabel": "Type",
  "asset.add.upload": "Add document",
  "asset.remove.confirmLabel": "Confirm removal",
  "asset.remove.confirmPrompt": "Really remove?",
  "asset.remove.yes": "Yes, remove",
  "asset.remove.no": "Cancel",
  "asset.promote.title": "Promote uploaded documents",
  "asset.promote.action": "Promote to document",
  // The honest degraded state: the promotable-documents fan-out failed. NOT the same as
  // "loaded, nothing to promote" — that stays silent.
  "asset.promote.failed.label": "Not loaded",
  "asset.promote.failed.message":
    "Your uploaded documents couldn't be loaded just now. Your documents above are complete — only the list to promote from is missing. Reload the page to try again.",
  "asset.promote.failed.reason": "Reason: {reason}",

  // ─── Workshops ────────────────────────────────────────────────
  "workshops.lead": "Find out what really fits you — one sharp question at a time. Three workshops build on each other.",
  "workshops.empty": "No workshops available.",
  "workshop.gate.locked": "locked",
  "workshop.gate.available": "ready",
  "workshop.gate.inProgress": "in progress",
  "workshop.gate.completed": "completed",
  "workshop.progress": "{answered}/{total} steps",
  "workshop.progress.stages": "{done}/{total}",
  "workshop.lockedBy": "Complete {name} first",
  "workshop.action.start": "Start",
  "workshop.action.resume": "Resume",
  "workshop.action.revisit": "Revisit",
  // W1 »Deine Werte« three-stage card (dashboard).
  "workshop.w1.stage.cardgame": "Card game",
  "workshop.w1.stage.interview": "Interview",
  "workshop.w1.stage.sektion": "Values section",
  "workshop.w1.detail.pyramidDone": "Pyramid stands",
  "workshop.w1.detail.interviewDone": "completed",
  "workshop.w1.detail.sektionDone": "stands",
  "workshop.w1.detail.round": "Round {round} of {total}",
  "workshop.w1.detail.open": "not yet started",
  // `workshop.w1.{resume,revisit,summary.gap,cue.*}` lived on the Workshops hub's inline W1
  // stage rail and were DELETED with it (2026-07-30) — the hub routes, the track presents
  // itself. `workshop.w1.stage.*` / `workshop.w1.detail.*` STAY: the left pane still renders
  // the three W1 stage entries. `workshop.w1.reader.lead` was already unused before this
  // round and is left alone rather than swept in on an unrelated change.
  "workshop.w1.reader.lead": "Your self-image, in your own words — read it back, expand any stage.",
  "workshop.runner.lead": "Answer only what sharpens the picture. Clear answers, skipping, and later correction are all valid.",
  "workshop.runner.notStarted": "This workshop hasn't started yet.",
  "workshop.runner.notFound.title": "This workshop doesn't exist",
  "workshop.runner.notFound.body": "This link doesn't lead to any of your workshops. Head back to the overview and pick one.",
  "workshop.runner.notFound.back": "To the workshops",
  "workshop.timeline.title": "Your path",
  "workshop.runner.kicker": "An interview about you",
  "workshop.findings.title": "What counts",
  "workshop.findings.lede": "What we infer from your answers. Correct anything that's off — each fix sharpens the match.",
  "workshop.findings.confirmed": "Confirmed",
  "workshop.findings.inferred": "Inferred",
  "workshop.findings.threads": "Open threads",
  "workshop.findings.empty": "Nothing yet — your answers will show up here.",
  "workshop.node.ueberlegung": "Reasoning",
  "workshop.node.skipped": "Skipped — already clear from your documents",
  "workshop.node.optional": "Optional",
  "workshop.node.yourAnswer": "Your answer",
  "workshop.node.produced": { one: "{n} statement", other: "{n} statements" },
  "workshop.node.pending": "Coming up",
  "workshop.card.generate": "Next question",
  "workshop.card.generating": "Thinking …",
  "workshop.card.best": "Strongest",
  "workshop.card.worst": "Most dispensable",
  "workshop.card.choose": "I'd choose",
  "workshop.card.situation": "Situation",
  "workshop.card.unsureLabel": "Unsure?",
  "workshop.card.submit": "Continue",
  "workshop.card.submitting": "Saving …",
  "workshop.card.skipOptional": "Skip and finish workshop",
  "workshop.card.veto": "Off the table",
  "workshop.card.notSure": "It depends",
  "workshop.card.narrativePlaceholder": "In your own words …",
  "workshop.card.whyLabel": "Care to say why? (optional)",
  "workshop.card.whyPlaceholder": "What this means to you…",
  "workshop.card.unsupported": "This card type isn't supported here yet.",
  "workshop.card.degraded": "This question couldn't be loaded.",
  "workshop.card.degradedSkip": "Skip this question",
  "workshop.consolidate.ready": "The required steps are done. Finish this workshop so the rest of the app can build on it.",
  "workshop.consolidate.action": "Finish workshop",
  "workshop.consolidate.busy": "Finishing …",
  "workshop.context.title": "Result",
  "workshop.context.themes": "Themes",
  "workshop.context.strengths": "Strengths",
  "workshop.context.gaps": "Gaps",
  "workshop.context.criteria": "Criteria",
  "workshop.context.antiValues": "Red lines",
  "workshop.narrative.degraded": "This is a preliminary version. For your fully written summary, generate it once more.",
  "workshop.targeting.title": "Edit search profile",
  "workshop.targeting.hint": "One entry per line.",
  "workshop.targeting.save": "Save",
  "workshop.targeting.roles": "Target roles",
  "workshop.targeting.queries": "Search terms",
  "workshop.targeting.seniority": "Seniority",
  "workshop.targeting.locations": "Locations",
  "workshop.targeting.exclusions": "Exclusions",

  // ─── Search ───────────────────────────────────────────────────
  "search.lead": "We search, verify and grade — you decide what's worth pursuing.",
  "search.start": "Start search",
  "search.starting": "Starting …",
  "search.stop": "Stop search",
  "search.stopping": "Stopping …",
  "search.config.title": "What we're looking for",
  "search.config.empty": "—",
  "search.targetingHint": "Sharpen your search profile first — the more you tell us, the better the matches.",
  "search.targetingHint.action": "To the search-profile workshop",
  // The identical-re-run gate: these exact parameters just came back with nothing.
  "search.unchanged.message":
    "This exact search just came back with nothing. Change your search parameters — otherwise you'd only get the same empty result.",
  "search.unchanged.action": "Adjust your search profile",
  "search.rail.title": "Search run",
  "search.rail.empty": "No run yet. Start a search.",
  // A failed run must not read as a quiet one — the rail names the failure and its reason.
  "search.run.failed.label": "Did not finish",
  "search.run.failed.message":
    "This search run broke off. Whatever it had already found is kept — start a new search to keep looking.",
  "search.run.failed.reason": "Reason: {reason}",
  "search.results.title": "Matching jobs",
  "search.results.empty": "No matches yet. Start a search.",
  "search.results.running": "We're looking for jobs that fit you …",
  // ─── The pipeline (S10c): ONE list, filtered — the results ARE the pool of
  //     applications still to send, so the vocabulary is a pipeline's, not a query's.
  "search.pipeline.title": "Your jobs",
  "search.pipeline.lead": "Sorted by fit. Save what's worth pursuing, hide the rest.",
  "search.filter.label": "Show",
  "search.filter.all": "All",
  "search.filter.saved": "Saved",
  "search.filter.urgent": "Closing soon",
  "search.filter.empty.saved": "Nothing saved yet. What you save collects here.",
  "search.filter.empty.urgent": "No deadline is close right now.",
  "search.urgent.title": "Closing soon",
  "search.urgent.lead": "These postings stop accepting applications within a week.",
  "search.refill": "Search for more jobs",
  "search.refill.hint": "Worked through your list? Start another run.",
  "search.company.postings": { one: "{n} job", other: "{n} jobs" },
  "search.company.via": "via {platform}",
  "search.company.logo": "{name} logo",

  // ─── Opportunity (card + detail) ──────────────────────────────
  "opportunity.tier.strong": "Strong fit",
  "opportunity.tier.viable": "Solid fit",
  "opportunity.tier.weak": "Weak fit",
  "opportunity.tier.unsuited": "Not a fit",
  "opportunity.tier.score": "{n}% match",
  "opportunity.stage.discovered": "found",
  "opportunity.stage.verified": "verified",
  "opportunity.stage.briefed": "summarised",
  "opportunity.stage.matched": "graded",
  "opportunity.jobState.open": "open",
  "opportunity.jobState.closed": "closed",
  "opportunity.jobState.unknown": "status unclear",
  "opportunity.line.verified": "We checked this role — {fit}, {state}.",
  "opportunity.salary": "Salary",
  "opportunity.bookmark": "Save",
  "opportunity.bookmarked": "Saved",
  "opportunity.generate": "Write an application",
  "opportunity.generating": "Writing …",
  "opportunity.dismiss": "Hide",
  "opportunity.delete": "Remove",
  "opportunity.open": "Details",
  "opportunity.posting.link": "To the posting",
  "opportunity.brief.title": "At a glance",
  "opportunity.brief.degraded": "This is a preliminary summary. Re-run the search for the fully written version.",
  "opportunity.match.title": "Why this job fits you",
  "opportunity.match.strength": "{n}%",
  "opportunity.dimension.fit": "Skills fit",
  "opportunity.dimension.social": "Culture & team",
  "opportunity.dimension.motivational": "Motivation",
  "opportunity.dimension.goal": "Goals & growth",
  "opportunity.detail.notFound": "This job doesn't exist.",
  "opportunity.detail.eyebrow": "Search · Job",
  "opportunity.detail.back": "Back to search",
  "opportunity.detail.removed": "You removed this job.",

  // ─── Signal tags (S9b + P-B) ──────────────────────────────────
  //     Warnings are SHORT labels, never written-out prose: the label goes on the card,
  //     the `.hint` is its hover title and the same sentence on the detail page. Every
  //     one of these reads a field that was already on the wire and was thrown away in
  //     the client fold (recon §9) — nothing here is newly inferred.
  "opportunity.flag.notReal": "Posting questionable",
  "opportunity.flag.notReal.hint": "The page doesn't look like a genuine job posting — check it before you invest time.",
  "opportunity.flag.sourceSuspicious": "Source unclear",
  "opportunity.flag.sourceSuspicious.hint": "Consent wall, re-post or scraped listing — we couldn't read the original page properly.",
  "opportunity.flag.fitMismatch": "Doesn't fit",
  "opportunity.flag.fitMismatch.hint": "The posting states a requirement that rules you out.",
  "opportunity.flag.fitBorderline": "Fit unclear",
  "opportunity.flag.fitBorderline.hint": "Some of it fits you, some doesn't — worth a closer read.",
  "opportunity.flag.closed": "No longer open",
  "opportunity.flag.closed.hint": "The page no longer shows a way to apply.",
  "opportunity.flag.stale": "Possibly out of date",
  "opportunity.flag.stale.hint": "We read the page and it looks like an old posting — worth checking whether the role is still being filled.",
  "opportunity.flag.outOfScope": "Outside your search",
  "opportunity.flag.outOfScope.hint": "The role doesn't match what you said you're looking for — it surfaced anyway, so you can judge it yourself.",
  "opportunity.flag.expired": "Deadline passed",
  "opportunity.flag.expired.hint": "The stated application deadline ({date}) is in the past.",
  "opportunity.flag.deadline": { one: "{n} day left", other: "{n} days left" },
  "opportunity.flag.deadline.hint": "Applications close on {date}.",
  "opportunity.flag.antiValues": "Against your red lines",
  "opportunity.flag.antiValues.hint": "Conflicts with what you ruled out: {items}. That's what cost this job points.",
  "opportunity.flag.lowConfidence": "Check unsure",
  "opportunity.flag.lowConfidence.hint": "We couldn't read the posting cleanly, so this grade is less reliable than usual.",
  "opportunity.tag.fit": "Skills fit",
  "opportunity.tag.social": "Culture fits",
  "opportunity.tag.motivational": "Motivating",
  "opportunity.tag.goal": "Goals fit",
  "opportunity.tag.salary": "Salary {value}",
  "opportunity.flags.title": "Worth knowing before you apply",
  "opportunity.assessment.title": "Overall",
  "opportunity.match.evidence": "What speaks for it",
  "opportunity.match.gap": "Still open",
  "opportunity.match.gapMark": "Gap",

  // ─── Organization ─────────────────────────────────────────────
  "org.summary.title": "About the employer",
  "org.summary.empty": "No employer information yet.",
  "org.evidence": { one: "{n} signal", other: "{n} signals" },
  "org.employer.label": "Employer",
  "org.signals.empty": "No citable employer signals yet.",
  "org.source.kununu": "Kununu",
  "org.source.trustpilot": "Trustpilot",
  "org.source.company": "Website",
  "org.source.news": "News",
  "org.source.posting": "Job posting",

  // ─── Profile facet correction (edit) ──────────────────────────
  "profile.edit": "Edit",
  "profile.edit.save": "Save",
  "profile.edit.cancel": "Cancel",
  "profile.identity.throughlineLabel": "Throughline",
  "profile.identity.selfDescriptionLabel": "Self-description",
  "profile.voice.title": "Your voice",
  "profile.voice.samples": "Writing samples (one per line)",
  "profile.voice.empty": "No writing samples yet.",

  // ─── W4 »Schreibstil« — the writing-style asset (P-A) ───────────────────────
  "writing.block.version": "Version {version} · created {date}",
  "writing.block.traits": "How you write",
  "writing.block.guidance": "What makes you recognisable",
  "writing.block.avoid": "What does not sound like you",
  "writing.block.sample": "In your own words",
  "writing.block.empty": "No writing style captured yet — without one your texts sound generic.",
  "writing.block.emptyCta": "Develop your writing style",
  "writing.trait.basis": "from {rounds}",
  "writing.confidence.low": "a first impression",
  "writing.confidence.medium": "reliable",
  "writing.confidence.high": "well evidenced",
  "writing.round.prose": "your free text",
  "writing.round.scenarios": "your style choices",
  "writing.round.validation": "your final correction",

  // ─── W4 »Schreibstil« — the three-round workshop runner (career-writing-style) ──
  "surface.writingStyle.title": "Your writing style",
  "writing.kicker": "Workshop ④ · Writing style",
  "writing.locked.lede":
    "Your values come first — this workshop invents situations out of YOUR material.",
  "writing.locked.blockedBy": "Still open: {name}",
  "writing.locked.cta": "To your values section",
  "writing.stage.prose": "① Your own words",
  "writing.stage.scenarios": "② Your style, compared",
  "writing.stage.validation": "③ Your final correction",
  "writing.gen.bullets": "Collecting bullet points from your own material …",
  "writing.gen.scenarios": "Inventing three situations for you …",
  "writing.gen.validation": "Writing a text in your style …",
  "writing.gen.failed": "That did not work. Nothing you wrote is lost.",
  "writing.gen.retry": "Try again",
  "writing.prose.groundingLabel": "Where this comes from:",
  "writing.prose.hint": "No test. A short paragraph — around 60 to 120 words — is enough; this is only about how you sound.",
  "writing.prose.placeholder": "Write the bullet points in your own words — the way you would tell someone.",
  "writing.prose.aria": "Your text for these bullet points",
  "writing.prose.submit": "Use this text",
  "writing.prose.submitting": "Saving …",
  "writing.scenarios.lede": "Two versions, same content. Which one sounds more like you?",
  "writing.scenarios.groundingLabel": "Why this situation",
  "writing.scenarios.pick": "Closer to me",
  "writing.scenarios.degraded": "This situation arrived without two versions — skip it and confirm the others.",
  "writing.scenarios.fitLabel": "How close is that to you?",
  "writing.scenarios.rewriteLabel": "Write it the way you would say it",
  "writing.scenarios.rewriteHint": "Optional, and the most valuable part: correct the guess in your own words.",
  "writing.scenarios.rewritePlaceholder": "In your words …",
  "writing.scenarios.submit": "Confirm all situations",
  "writing.scenarios.submitting": "Saving …",
  "writing.scenarios.progress": "{done} of {total} answered",
  "writing.scenarios.answered": "{n} situations decided",
  "writing.fit.trifft": "Spot on",
  "writing.fit.fast": "Almost",
  "writing.fit.daneben": "Off",
  "writing.validation.lede": "This text is written in your style. Does that hold — or would you change it?",
  "writing.validation.contextLabel": "Written for:",
  "writing.validation.confirm": "Fits — record my style",
  "writing.validation.confirming": "Recording …",
  "writing.validation.edit": "Change it",
  "writing.validation.editHint": "Your version wins — it becomes the benchmark, not the proposal.",
  "writing.validation.save": "Use my version",
  "writing.validation.cancel": "Cancel",
  "writing.validation.answered": "You confirmed your style.",
  "writing.node.change": "change",
  "writing.node.changeAria": "Answer {round} again",
  "writing.done.title": "Your writing style is set",
  "writing.done.hint": "From now on your letters and short profiles sound like you.",
  "profile.workmode.remote": "Remote",
  "profile.workmode.hybrid": "Hybrid",
  "profile.workmode.onsite": "On-site",

  // ─── Kompass »Profil« — the unified digital-asset document ─────────────────
  "profile.doc.kicker": "Compass",
  "profile.doc.warum": "Your why",
  "profile.doc.warumGap": "Your why becomes sharper in Workshop 1 — it belongs right here.",
  "profile.doc.werte": "Your values",
  // »Was dich antreibt« — the ProfileFacet arrays (motivations / drives / anti-values / no-gos).
  "profile.doc.antrieb": "What drives you",
  "profile.doc.antriebHint": "What pulls you in and what pushes you away — both count when a role has to fit.",
  // »Wohin du willst« — ProfileTrajectory, the document's forward-looking section.
  "profile.doc.richtung": "Where you're heading",
  "profile.doc.richtung.now": "Right now",
  "profile.doc.richtung.mid": "Mid-term",
  "profile.doc.richtung.long": "Long-term",
  // Asset origin lines (P-A/R6): one truth, said out loud so nobody counts it twice.
  "profile.doc.rahmenOrigin": "Edit them here or in your search profile — it's the same entry:",
  "profile.doc.rahmenEmpty": "Note where and on what terms you want to work — your results get noticeably better.",
  "profile.doc.stimmeOrigin": "Writing samples you keep by hand. If a writing style exists, it takes precedence.",
  "profile.doc.schreibstil": "Your writing style",
  "profile.doc.schreibstilOrigin": "Derived from the writing-style workshop — the same version steers your cover letters.",
  "profile.doc.positionierung": "Your positioning",
  "profile.doc.philosophie": "Your work philosophy",
  "profile.doc.vision": "Your vision",
  "profile.doc.identitaet": "Your identity",
  "profile.doc.rahmen": "Your framework conditions",
  "profile.doc.stimme": "Your voice",
  "profile.doc.kandidatenprofil": "Your candidate profile",
  "profile.doc.related": "Related",
  "profile.doc.suchprofil": "Your search profile →",
  "profile.doc.suchprofilHint": "What you're looking for — derived from this compass.",
  "profile.doc.buildTitle": "Build your compass",
  "profile.doc.buildLead": "Your why, your values and how you come across — in one place. Start with Workshop 1.",

  // ─── Applications (slice 5) ────────────────────────────────────────────────
  "surface.bewerbungen.lead": "Turn a matched job into an application that sounds like you.",
  "applications.list.title": "Your applications",
  // S11 — the empty board never shoves a picker forward. It speaks ONLY when it knows
  // something the hero does not: that saved jobs are already waiting to be written up.
  "applications.empty.ready": {
    one: "{n} saved job is waiting — pick it below and we'll write the application.",
    other: "{n} saved jobs are waiting — pick one below and we'll write the application.",
  } as MessageValue,
  "applications.new": "New application",
  "applications.pick.title": "Which job are you applying for?",
  "applications.pick.placeholder": "Choose a job …",
  "applications.pick.available": { one: "{n} saved job without an application", other: "{n} saved jobs without an application" } as MessageValue,
  "applications.pick.none": "Save a job first — then we'll write the application for it.",
  "applications.pick.allApplied": "Every job you saved already has an application. Save another one in your search.",
  "applications.pick.toSearch": "To your search",
  "applications.generate": "Write my application",
  "applications.generating": "Writing for you …",
  "applications.open": "Open",
  "applications.rail.title": "Your application is taking shape",
  "applications.rail.empty": "Start an application to watch it come together.",
  "applications.stop": "Stop",
  "applications.status.draft": "Draft",
  "applications.status.in_review": "In review",
  "applications.status.changes_requested": "Changes requested",
  "applications.status.approved": "Approved",
  "applications.status.sent": "Sent",
  "applications.gen.stage.preselecting": "Choosing what to say",
  "applications.gen.stage.authoring": "Writing the letter",
  "applications.gen.stage.judging": "Reviewing the letter",
  "applications.gen.stage.kurzprofil": "Writing the short profile",
  "applications.gen.stage.tailoring": "Tailoring the CV",
  "applications.gen.stage.emailing": "Writing the e-mail",
  "applications.gen.stage.done": "Done",
  "applications.gen.status.running": "Running",
  "applications.gen.status.completed": "Completed",
  "applications.gen.status.stopped": "Stopped",
  "applications.gen.status.failed": "Failed",
  "applications.gen.iterations": { one: "{n} review round", other: "{n} review rounds" } as MessageValue,
  "applications.gen.converged": "Passed review",
  "applications.gen.notConverged": "Needs your eye",
  "review.back": "Back to applications",
  "review.notFound": "This application doesn't exist (yet).",
  "review.generating": "Your application is taking shape …",
  // The run in flight — the stage timeline that stands where the letter will be.
  "review.progress.label": "Progress of your application",
  "review.progress.lead": "This takes a few minutes — you can leave this page, the work carries on.",
  // The run failed — honest heading, the technical message, the way forward.
  "review.failed.title": "We couldn't finish this application",
  "review.failed.lead": "Something broke while writing. A second attempt usually works — your job and your evidence are untouched.",
  "review.failed.detail": "Technical detail: {message}",
  "review.failed.retry": "Try again",
  "review.failed.retrying": "Starting over …",
  "review.letter.title": "Cover letter",
  "review.letter.degraded": "A written draft couldn't be created — this is a plain fallback. Regenerate to try again.",
  "review.kurzprofil.title": "Short profile",
  "review.email.title": "Accompanying e-mail",
  "review.gates.title": "Quality review",
  "review.gate.evidence": "Evidence",
  "review.gate.roleFit": "Role fit",
  "review.gate.motivation": "Motivation",
  "review.gate.clarity": "Clarity",
  "review.gate.antiSlop": "Own voice",
  "review.gate.readerTest": "Readability",
  "review.gate.pass": "OK",
  "review.gate.fail": "Check",
  "review.annotations.title": "Review notes",
  // The AI's review notes as in-context annotations on the letter: a severity vocabulary,
  // the finding block under the marked passage, and the two answers you can give it.
  "review.severity.blocker": "Blocker",
  "review.severity.major": "Serious",
  "review.severity.minor": "Minor",
  "review.severity.nit": "Nitpick",
  "review.severity.positive": "Positive",
  "review.finding.label": "Review note on {gate}",
  "review.finding.suggestion": "Suggestion",
  "review.finding.apply": "Take the suggestion",
  "review.finding.applying": "Taking it over …",
  "review.finding.dismiss": "Dismiss",
  "review.finding.resolved": "Done",
  "review.finding.open": "Show the review note",
  "review.tailoring.title": "Tailored CV",
  "review.tailoring.emphasis": "highlighted",
  "review.attachments.title": "Enclosures",
  "review.smartlinks.title": "Links",
  "review.edit.propose": "Suggest a change",
  "review.edit.save": "Suggest",
  "review.edit.cancel": "Cancel",
  "review.edit.approve": "Apply",
  "review.edit.reject": "Discard",
  "review.edit.pending": "Suggested changes",
  "review.lifecycle.title": "Status",
  "review.lifecycle.toReview": "Send to review",
  "review.lifecycle.approve": "Approve",
  "review.lifecycle.requestChanges": "Request changes",
  "review.lifecycle.markSent": "Mark as sent",
  "review.lifecycle.reopen": "Reopen",
  "review.download": "Download as ZIP",

  // ─── Runs (§8 — the AI-run trace surface) ─────────────────────────────
  "surface.laeufe.lead": "Every AI call, disclosed turn by turn — model, engine, tokens, params, prompt and estimated cost.",

  // ─── LLM settings (§9.1) — per-integration model + params override ───
  "settings.hint": "Override the model and sampling parameters per AI integration. Anything left blank uses the deployed default. Only the fields you set are saved.",
  "settings.loading": "Loading settings…",
  "settings.error": "Error: {message}",
  "settings.retry": "Retry",
  "settings.model": "Model",
  "settings.model.default": "default",
  "settings.temperature": "Temperature",
  "settings.topP": "Top-p",
  "settings.maxTokens": "Max tokens",
  "settings.reasoning": "Reasoning effort",
  "settings.reasoning.none": "Default",
  "settings.reasoning.low": "Low",
  "settings.reasoning.medium": "Medium",
  "settings.reasoning.high": "High",
  "settings.param.default": "Default",
  "settings.current": "Effective: {model}",
  "settings.override": "Overridden",
  "settings.save": "Save",
  "settings.reset": "Reset",
  "settings.saved": "Saved",
  "settings.saveFailed": "Save failed",

  // ─── The opt-in human-review gate (§9.2) — off unless YOU switch it on ────
  "settings.review.label": "Ask me before every AI call",
  "settings.review.hint": "Off by default. While on, each of your own AI calls stops before it runs and shows you the full prompt, the parameters and the model — you can edit them, then let the call go or cancel it. Only your calls are affected; other people never wait.",

  // The review pane itself (shell-mounted; only ever visible while a call of yours waits).
  "hil.title": "An AI call is waiting for you",
  "hil.origin": "{seam} · from {action}",
  "hil.seam.unknown": "unnamed step",
  "hil.action.unknown": "unknown action",
  "hil.binding": "Runs on {engine} · {model}",
  "hil.deadline": "{seconds}s left before this call is dropped",
  "hil.system": "System prompt",
  "hil.user": "Message",
  "hil.model": "Model",
  "hil.model.other": "Other model (type it)…",
  "hil.model.otherPlaceholder": "e.g. sonnet",
  "hil.model.otherNote": "Not on the allowlist. It only runs if the engine knows this name itself — a subscription engine has its own (claude answers to \u0022sonnet\u0022).",
  "hil.model.unlisted": "{model} (not on the allowlist)",
  "hil.model.unlistedNote": "This call\u0027s own model is not on the allowlist — keep it, or pick one from the list.",
  "hil.temperature": "Temperature",
  "hil.maxTokens": "Max tokens",
  "hil.param.unset": "not set",
  "hil.schema": "Response format (read-only)",
  "hil.approve": "Run it",
  "hil.reject": "Cancel the call",
  "hil.sending": "Sending…",
  "hil.queue": "{n} more waiting",
  "hil.error": "Error: {message}",

  "runs.loading": "Loading runs…",
  "runs.empty": "No AI runs yet. Once you extract documents, run a workshop or a search, every AI call appears here — with prompt, params, tokens and cost.",
  "runs.retry": "Try again",
  "runs.ledger.runs": "Runs",
  "runs.ledger.calls": "AI calls",
  "runs.ledger.tokens": "Tokens",
  "runs.ledger.credits": "Credits used",
  "runs.ledger.total": "Provider cost",
  "runs.credits.hint": "billed unit",
  "runs.credits": "{n} credits",
  "runs.creditsOne": "{n} credit",
  "runs.history.title": "History",
  "runs.models.title": "Models used",
  "runs.models.avgLatency": "⌀ {ms} ms",
  "runs.calls": "{n} calls",
  "runs.callsOne": "{n} call",
  "runs.engine.claude": "Claude · Sub",
  "runs.engine.codex": "Codex · Sub",
  "runs.engine.openrouter": "OpenRouter",
  "runs.engine.synthetic": "Deterministic",
  "runs.role.system": "System prompt",
  "runs.role.user": "User message",
  "runs.role.assistant": "Assistant reply",
  "runs.activity.default": "AI run",
  "runs.activity.search": "Job search",
  "runs.activity.ingest": "Document extraction",
  "runs.activity.workshop": "Workshop consolidation",
  "runs.activity.workshopCard": "Workshop · card generation",
  "runs.activity.generate": "Application generation",
  "runs.label.extract": "Extraction",
  "runs.label.verify": "Verification",
  "runs.label.match": "Match",
  "runs.label.orgResearch": "Employer",
  "runs.label.orgSummary": "Employer summary",
  "runs.label.brief": "Brief",
  "runs.label.triage": "Triage",
  "runs.label.preselect": "Preselect",
  "runs.label.author": "Authoring",
  "runs.label.kurzprofil": "Short profile",
  "runs.metric.input": "Input",
  "runs.metric.output": "Output",
  "runs.metric.reasoning": "Reasoning",
  "runs.metric.cache": "Cache hit",
  "runs.metric.latency": "Latency",
  "runs.metric.tokens": "Tokens",
  "runs.metric.yes": "yes",
  "runs.cost.title": "Provider cost (EUR, internal)",
  "runs.cost.rate": "Rate {rate} · source USD",
  "runs.cost.input": "Input",
  "runs.cost.output": "Output",
  "runs.cost.total": "Total",
  "runs.cost.estimated": "Split estimated by token share (no per-token prices available)",
  "runs.cost.cacheNote": "cache tokens weighted — caching lowers the real cost",
  "runs.cost.free": "Free (subscription engine · marginal cost €0)",
  "runs.cacheChip": "{n} cache tokens",
  "runs.params.title": "Parameters",
  "runs.params.none": "No parameters captured.",
  "runs.prompt.title": "Prompt",
  "runs.prompt.truncatedTag": "shortened",
  "runs.prompt.truncatedNote": "Very large prompts are stored shortened to spare the event store.",
  "runs.prompt.none": "No prompt captured (an earlier run, or a seam without prompt capture).",
  "runs.prompt.copy": "Copy",
  "runs.prompt.copyAll": "Copy all",
  "runs.prompt.copied": "Copied",
  "runs.prompt.copyFailed": "Copy failed",
  "runs.completion.title": "Model response",
  "runs.completion.subtitle": "Raw reply",
  "runs.completion.truncatedTag": "shortened",
  "runs.completion.truncatedNote": "Very large replies are stored shortened to spare the event store.",
  "runs.why": "Why this prompt",
  "runs.rerun.open": "Repeat this call",
  "runs.rerun.hint": "Runs this exact prompt again. A repeat is captured as its own run and costs credits; the original stays untouched.",
  "runs.rerun.model": "Model",
  "runs.rerun.instructions": "Extra instruction (optional)",
  "runs.rerun.instructionsPlaceholder": "e.g. ask more concretely about situations",
  "runs.rerun.go": "Run",
  "runs.rerun.running": "Running…",
  "runs.rerun.result.valid": "Done — the reply matched the expected format.",
  "runs.rerun.result.invalid": "Done — but the reply did not match the expected format.",
  "runs.rerun.result.unchecked": "Done — the format was not checked for this step.",
  "runs.rerun.result.failed": "The repeat failed.",
  "runs.rerun.noSchema": "repeated without the stored response format",
  // The review marks (§9.2) — a call a human approved, and whether they rewrote it first.
  "runs.reviewed.plain": "approved",
  "runs.reviewed.edited": "approved · edited",
  "runs.reviewed.hint": "You saw this call before it ran and let it go unchanged.",
  "runs.reviewed.editedHint": "You edited this call before it ran — the prompt and parameters shown here are yours, not the step's own.",

  // ─── Credits (Step 5 — buy-credits surface, balance chip, insufficient-balance notice) ───
  "nav.guthaben": "Credits",
  "surface.credits.title": "Credits",
  "credits.lead": "Your credits power your searches and applications. Top up whenever you like.",
  "credits.balance.label": "Your balance",
  "credits.balance.unit": { one: "{n} credit", other: "{n} credits" } as MessageValue,
  "credits.balance.loading": "Loading your balance …",
  "credits.balance.error": "We couldn't load your balance. Please try again.",
  "credits.balance.retry": "Try again",
  "credits.pack.name": "1,000 credits",
  "credits.pack.priceLine": "€10.00 incl. VAT",
  "credits.pack.cta": "Top up balance",
  "credits.pack.starting": "Opening secure checkout …",
  "credits.pack.secure": "You pay securely on Stripe's page — we never see your card details.",
  "credits.unavailable.title": "Top-up isn't available right now",
  "credits.unavailable.body": "Buying credits is briefly unavailable. Please try again in a little while.",
  "credits.checkout.error": "We couldn't start the payment. Please try again.",
  "credits.return.success.title": "Payment received",
  "credits.return.success.body": "Your credits are being added …",
  "credits.return.success.slow": "This is taking a little longer than usual — your credits will be added as soon as the payment is confirmed.",
  "credits.return.success.done": "Your balance has been topped up.",
  "credits.return.cancelled.title": "Purchase cancelled",
  "credits.return.cancelled.body": "No payment was made. You can top up whenever you're ready.",
  "credits.notice.insufficient": "You don't have enough credits for that.",
  "credits.notice.blocked": "Top up your balance to get started.",
  "credits.notice.cta": "Top up balance",
  "credits.notice.dismiss": "Dismiss",
  "session.expired.message": "Your session has expired — sign in again, your progress is kept.",
  "session.expired.relogin": "Sign in again",
  "credits.chip.label": "Credits",
  "credits.chip.aria": { one: "Balance: {n} credit — top up", other: "Balance: {n} credits — top up" } as MessageValue,

  // ─── Werte-Kartenspiel (W1 redesign §3, V2) ──────────────────────────────
  "surface.valuesCardgame.title": "Value card game",
  "valuecard.kicker": "Workshop 1 · Your values",
  "valuecard.intro.lede":
    "Discover your personal values by comparison. You'll weigh cards against each other, narrow 62 down to 8, then choose the one that carries your life, three that make you strong, and four you build on.",
  "valuecard.intro.start": "Start",
  "valuecard.legend": "Colour families",
  "valuecard.family.blau": "Blue",
  "valuecard.family.gruen": "Green",
  "valuecard.family.gelb": "Yellow",
  "valuecard.family.rot": "Red",
  "valuecard.reduce.prompt": "What means more to you?",
  "valuecard.reduce.vs": "or",
  "valuecard.reduce.count": "{n} cards left",
  "valuecard.reduce.empty": "Nothing left to compare.",
  "valuecard.refine.title": "Fine-tune",
  "valuecard.refine.hint": "These are your eight. Swap any of them for a card you set aside — or continue.",
  "valuecard.refine.budget": "{n} swaps left",
  "valuecard.refine.rescue": "Swap in instead",
  "valuecard.refine.unsureTitle": "Your unsure cards",
  "valuecard.refine.noUnsure": "You weren't unsure anywhere — so here's everything you set aside.",
  "valuecard.refine.noneLeft": "Nothing left to swap in.",
  "valuecard.refine.done": "Continue",
  "valuecard.lebenswert.title": "Your life value",
  "valuecard.lebenswert.hint": "Which one value do you work at your whole life — what do you strive for most?",
  "valuecard.superkraefte.title": "Your superpowers",
  "valuecard.superkraefte.hint": "Which three values let you make the biggest difference?",
  "valuecard.superkraefte.count": "{n} of {total} chosen",
  "valuecard.confirm.title": "Your value pyramid",
  "valuecard.confirm.hint": "Does this feel right? Confirm to keep it — you can lay a new pyramid anytime.",
  "valuecard.confirm.commit": "Confirm pyramid",
  "valuecard.confirm.committing": "Saving…",
  "valuecard.tier.lebenswert": "Life value",
  "valuecard.tier.superkraefte": "Superpowers",
  "valuecard.tier.basiswerte": "Base values",
  "valuecard.action.next": "Continue",
  "valuecard.action.back": "Back",
  "valuecard.done.title": "Your value pyramid",
  "valuecard.done.hint": "This is the pyramid you laid. You can lay a new one anytime — the old one stays valid history.",
  "valuecard.done.restart": "Lay a new pyramid",
  "valuecard.done.toInterview": "Continue to the interview",
  // Step path (persistent) + per-phase guidance + reduce progress detail.
  "valuecard.steps.label": "Game steps",
  "valuecard.step.reduce": "Reduce",
  "valuecard.step.refine": "Fine-tune",
  "valuecard.step.lebenswert": "Life value",
  "valuecard.step.superkraefte": "Superpowers",
  "valuecard.step.confirm": "Confirm",
  "valuecard.step.status": "Step {n} of {total}: {label}",
  "valuecard.guide.reduce": "Tap the value that means more to you.",
  "valuecard.guide.refine": "Swap any of your eight for a card you set aside — or keep them. Next: your one life value.",
  "valuecard.guide.lebenswert": "Choose the one value you strive for most. Next: three superpowers.",
  "valuecard.guide.superkraefte": "Choose the three values you make the biggest difference with. Next: confirm.",
  "valuecard.guide.confirm": "Check your pyramid and confirm — you can lay a new one anytime.",
  "valuecard.reduce.round": "Round {n}",
  "valuecard.reduce.pairsLeft": "{n} comparisons left this round",
  "valuecard.reduce.unsure": "Not sure",
  "valuecard.reduce.unsureOn": "Unsure mode: pick the card that just barely wins — the other one is kept to rescue later.",
  "valuecard.reduce.choose": "Choose »{name}«",
  "valuecard.reduce.firstHint": "How it works: tap the value that matters more. »Not sure« keeps it but sets the other one aside.",
  "valuecard.reduce.delayedHint": "Would you rather a life full of {x} and without {y} — or a life full of {y} and without {x}?",
  "valuecard.reduce.delayedHintMit": "Would you rather a life full of {x} and without {y} — or a life full of {y} and without {x}?",
  "valuecard.kbd.reduce": "Keyboard: ← → or 1 / 2 to choose · U for unsure",
  "valuecard.kbd.pick": "Keyboard: arrow keys to move · Enter to pick",
  "valuecard.progress.label": "Reduction progress",

  // ─── Werte-Interview (W1 redesign §4–§5, V3/V4) ───
  "surface.valuesInterview.title": "Your values, in depth",
  "interview.kicker": "Workshop 1 · Your values",
  "interview.intro.needPyramid": "First lay your value pyramid in the card game. The interview builds on it.",
  "interview.intro.toCardgame": "To the card game",
  "interview.intro.lede":
    "Now we go deeper. A few sharp questions over your own values turn the pyramid into a picture you can actually use — read against an accepted model of human values, honestly and with stated confidence.",
  "interview.intro.plan": "Plan the interview",
  "interview.intro.planning": "Planning…",
  "interview.card.generate": "Show the question",
  "interview.card.generating": "Thinking…",
  "interview.card.submit": "Confirm",
  "interview.card.submitting": "Saving…",
  "interview.card.best": "Most",
  "interview.card.worst": "Least",
  "interview.card.choose": "I choose",
  "interview.card.veto": "Never",
  "interview.card.retry": "Try again",
  "interview.round.preparing": "I'm putting your questions together…",
  "interview.round.thinking": "I'm reading your answers and thinking about what to ask next…",
  "interview.round.failed": "This question couldn't be created.",
  "interview.round.stillWorking": "Your questions are still being made — they'll appear here on their own.",
  "interview.card.openPlaceholder": "In your own words…",
  "interview.card.why": "Why this question",
  "interview.card.whyLabel": "Care to say why? (optional)",
  "interview.card.whyPlaceholder": "What this means to you…",
  "interview.card.needsText": "Write a sentence first — then I can save it.",
  "interview.card.saveFailed": "That could not be saved: {message}",
  "interview.reward.eyebrow": "Your value picture",
  "interview.block.skipped": "Already clear from your documents.",
  "interview.history.title": "Your signals so far",
  "interview.done.title": "That's your picture",
  "interview.done.hint": "Every question is answered. Your signals feed the summary of your values.",
  // PARKED with app/lib/interview-readout.ts — no longer rendered on the interview surface.
  "interview.readout.title": "Your signals",
  "interview.readout.hint": "How well each dimension is covered so far — honest, never overclaimed.",
  "interview.readout.values": "Values (Schwartz)",
  "interview.readout.workstyle": "Working style (Big Five)",
  "interview.done.toSektion": "Compose my values section",
  "interview.rail.adjusted": "Plan adjusted",
  "interview.node.change": "change",
  "interview.node.changeAria": "Change your answer to “{title}”",
  "interview.node.recall": "view the question",

  // ─── Werte-Sektion — the culminating statements pendant (V5) ───
  "surface.werteSektion.title": "Your values section",
  "sektion.kicker": "Workshop 1 · Your values",
  "sektion.intro.needPyramid": "First lay your value pyramid and answer the interview — the summary builds on them.",
  "sektion.intro.toCardgame": "To the card game",
  "sektion.intro.lede": "From your pyramid and your signals we draft your values in depth — and four statements about you. You confirm and edit everything before it is saved.",
  "sektion.intro.synthesize": "Draft my values section",
  "sektion.intro.synthesizing": "Composing…",
  "sektion.resume.offer": "Your last draft is ready — pick up where you left off.",
  "sektion.resume.action": "Restore draft",
  "sektion.resume.waiting": "Your section is still being composed — one moment.",
  "sektion.resynthesize": "Draft again",
  "sektion.statements.title": "Your statements",
  "sektion.values.title": "Your top values in depth",
  "sektion.statement.warum": "My why",
  "sektion.statement.positionierung": "My positioning",
  "sektion.statement.philosophie": "My work philosophy",
  "sektion.statement.vision": "My vision",
  "sektion.value.definition": "What this means for me",
  "sektion.value.leitsatz": "My guiding principle",
  "sektion.gap": "Still open — sharpen this in conversation.",
  "sektion.confirm": "Confirm my values section",
  "sektion.confirming": "Saving…",
  "sektion.saved": "Saved. Your values section is part of your compass.",
  "sektion.edit": "Edit",
  // ─── Editorial document surface (V5 rebuild) ───
  "sektion.doc.values": "Your values",
  "sektion.doc.valuesUnit": "values",
  "sektion.doc.aufEinenBlick": "At a glance",
  "sektion.doc.expandHint": "Show what this means for me",
  "sektion.review.badge": "Draft",
  "sektion.review.note": "Draft — read and edit everything before you confirm it.",
  "sektion.edit.save": "Apply",
  "sektion.edit.cancel": "Discard",
  "sektion.edit.aria": "Edit this passage",
  "sektion.refine.open": "Feedback",
  "sektion.refine.aria": "Ask AI to refine this passage",
  "sektion.refine.placeholder": "What should be different? e.g. shorter, bolder, fewer buzzwords…",
  "sektion.refine.submit": "Get a suggestion",
  "sektion.refine.submitting": "Thinking…",
  "sektion.refine.cancel": "Cancel",
  "sektion.proposal.label": "Suggestion",
  "sektion.proposal.accept": "Apply",
  "sektion.proposal.discard": "Discard",
  "sektion.proposal.again": "Try other feedback",

  // ─── Prozess-Landkarten (Admin-Konsole, `/console/processes/:slug`) ───
  // The authored per-use-case AI process graphs: what happens, on which seat, with which
  // prompt, and which real captured calls landed under each node.
  "surface.prozesse.title": "Process maps",
  "surface.prozesse.lead":
    "One authored map per use case: what the person does, which seat runs, which prompt it sends — and the real calls that landed under each step.",
  "processes.loading": "Loading process maps…",
  "processes.retry": "Load again",
  "processes.empty": "No process map has been authored yet.",
  "processes.maps.title": "Process maps",
  "processes.graph.aria": "Process graph: {title}",
  "processes.legend.title": "Legend",
  "processes.kind.userStep": "Step by the person",
  "processes.kind.aiSeam": "AI seat",
  "processes.kind.event": "Event",
  "processes.kind.inputAsset": "Input asset",
  "processes.deterministic": "deterministic",
  "processes.edge.flow": "Flow",
  "processes.edge.repair": "Repair (asks itself again)",
  "processes.edge.fallback": "Fallback (failure path)",
  "processes.select.hint": "Pick a node to see its seat, its prompt and its real runs.",
  "processes.detail.route": "Route",
  "processes.seam.title": "Seat",
  "processes.seam.none": "No configurable seat — this node calls no model.",
  "processes.seam.unresolved": "This seat is named by the map but was not resolved at boot.",
  "processes.prompt.title": "Prompt",
  "processes.prompt.system": "System",
  "processes.prompt.user": "User scaffold",
  "processes.prompt.none": "This node sends no prompt.",
  "processes.prompt.missing": "Prompt {id} is referenced but was not delivered.",
  "processes.prompt.runtimeMark": "[grounding appended at runtime]",
  "processes.prompt.runtimeNote":
    "At the marked place the runtime appends German grounding doctrine — the live prompt is longer than what is shown here.",
  "processes.traces.title": "Real runs",
  "processes.traces.count": "{shown} of {total}",
  "processes.traces.none": "No captured run for this node yet.",
  "processes.traces.noFilter":
    "This seat writes no activity of its own, so there is no honest filter separating its runs from the successful call's. What gives it away is in the note above.",
  "processes.traces.noModelCall": "No model call — there are no runs for this node.",
  "processes.traces.unavailable": "The runs could not be loaded: {message}",
  "processes.trace.tokens": "In {input} · Out {output}",
  "processes.trace.completion": "Model reply",
} as const

/** German overrides — the text the user actually reads (de-DE). */
const de: Partial<Record<keyof typeof catalog, MessageValue>> = {
  "app.name": "Career",
  "app.tagline": "Finde Stellen, die zu dir passen. Schreib Bewerbungen, die nach dir klingen.",

  "nav.onboarding": "Onboarding",
  "nav.review": "Vorschläge",
  "nav.workshops": "Workshops",
  "nav.kompass": "Kompass",
  "nav.suche": "Suche",
  "nav.bewerbungen": "Bewerbungen",
  "nav.laeufe": "Läufe",
  "nav.prozesse": "Prozesse",
  "nav.einstellungen": "KI-Einstellungen",
  "nav.design": "Design & Theme",
  "nav.herzschlag": "Herzschlag",
  "nav.mehr": "Mehr",
  "nav.profil": "Profil",
  "nav.lebenslauf": "Lebenslauf",
  "nav.belege": "Belege",
  "nav.nachweise": "Nachweise",
  "a11y.skipToContent": "Zum Inhalt springen",

  "auth.login": "Anmelden",
  "auth.logout": "Abmelden",
  "auth.devLogin": "Dev-Anmeldung",
  "auth.signedInAs": "Angemeldet als {name}",

  // ─── App-bar chrome (theme toggle + resource metadata) ────────
  "theme.label": "Farbschema",
  "theme.light": "Hell",
  "theme.dark": "Dunkel",
  "theme.auto": "Auto",
  "bar.nav.label": "Bereiche",
  "bar.resourcePath.label": "Ressourcenpfad",
  "meta.open": "Details zur Ansicht",
  "meta.title": "Diese Ansicht",
  "meta.resource": "Ansicht",
  "meta.owner": "Eigentümer",
  "meta.visibility": "Sichtbarkeit",
  "meta.sharing": "Geteilt mit",
  "meta.visibility.private": "Privat",
  "meta.sharing.none": "Nur du",

  // ─── Top navbar: breadcrumb + user menu (§7a) ─────────────────
  "breadcrumb.label": "Du bist hier",
  "breadcrumb.switchStage": "Bereich wechseln",
  "breadcrumb.switchSection": "Abschnitt wechseln",
  "breadcrumb.appSpace": "Bereich wechseln",
  "breadcrumb.step": "Zum Schritt springen",
  "user.menu.open": "Kontomenü",
  "user.menu.title": "Konto",
  "workspace.default": "Standard-Arbeitsbereich",
  "breadcrumb.workshop.self-knowledge": "Workshop 1 · Selbstbild",
  "breadcrumb.workshop.search-profile": "Workshop 2 · Suchprofil",
  "breadcrumb.workshop.profiler": "Workshop 3 · Profiler",

  "login.title": "Bei Career anmelden",
  "login.body": "Career hilft dir, Stellen zu finden, die zu dir passen \u2014 und Bewerbungen zu schreiben, die nach dir klingen. Melde dich mit deinem Platform-Konto an.",
  "login.devHint": "Entwicklungsmodus: Test-Sitzung aktiv (ohne Keycloak).",
  "login.cta": "Mit Platform-Konto anmelden",
  "brand.name": "Career Studio",
  "nav.collapseSidebar": "Seitenleiste einklappen",
  "nav.expandSidebar": "Seitenleiste ausklappen",
  "aside.toggle": "Detailbereich umschalten",
  "aside.title": "Details",
  "aside.empty": "Kontextinfos zur aktuellen Ansicht erscheinen hier.",

  // ─── Linke Leiste: Abschnitte im aktuellen Bereich (§7) ───────
  "sectionnav.label": "Abschnitte",
  "sectionnav.empty": "In diesem Bereich gibt es noch keine Abschnitte.",
  "sectionnav.overview": "Übersicht",
  "sectionnav.ws.self": "Selbstbild",
  "sectionnav.ws.search": "Suchprofil",
  "sectionnav.ws.profiler": "Entscheidungsprofil",
  "sectionnav.ws.writing": "Schreibstil",
  "sectionnav.kompass.doc": "Mein Kompass",
  "sectionnav.onboarding.upload": "Unterlagen",
  "sectionnav.indicator.open": "{n} offen",
  "sectionnav.indicator.cvDecisions": {
    one: "{n} offene Entscheidung zu deinem Lebenslauf",
    other: "{n} offene Entscheidungen zu deinem Lebenslauf",
  },
  "sectionnav.indicator.evidenceGaps": {
    one: "{n} Aussage noch ohne Beleg",
    other: "{n} Aussagen noch ohne Beleg",
  },
  "sectionnav.state.done": "erledigt",
  "sectionnav.state.current": "aktuell",
  "sectionnav.state.locked": "gesperrt",
  "sectionnav.state.open": "offen",
  "sectionnav.state.attention": "wartet auf dich",
  "sectionnav.locked.afterCardgame": "Erst das Kartenspiel abschließen",
  "sectionnav.locked.afterInterview": "Erst das Interview abschließen",
  "sectionnav.locked.afterSektion": "Erst die Werte-Sektion abschließen",
  "sectionnav.locked.afterSearchProfile": "Erst das Suchprofil abschließen",
  "sectionnav.detail.round": "Runde {round} von {total}",
  "sectionnav.stale.badge": "Veraltet",
  "sectionnav.stale.hint": "Alte Ansicht — Neugestaltung folgt",
  "sectionnav.account": "Konto",

  // ─── Rechte Leiste: Platform-KI-Agent ─────────────────────────
  "ai.panel.title": "Platform-KI",
  "ai.panel.subtitle": "Hilfe für alles, was du auf der Platform machst.",
  "ai.panel.toggle": "Platform-KI ein- oder ausblenden",
  "ai.panel.short": "KI",
  "ai.panel.apps": "Deine Anwendungen",
  "ai.panel.thisApp": "Diese App",
  "ai.panel.moreSoon": "Weitere Anwendungen erscheinen hier, sobald du Zugriff darauf hast.",
  "ai.panel.composerPlaceholder": "Frag die Platform-KI …",
  "ai.panel.comingSoon": "Bald verfügbar",

  "notfound.title": "Seite nicht gefunden",
  "notfound.body": "Diese Adresse existiert nicht.",
  "notfound.back": "Zurück zur Suche",

  "common.comingSoon": "Dieser Bereich kommt bald.",
  "gated.hint": "Mit entsprechender Berechtigung verfügbar.",

  "surface.onboarding.title": "Onboarding",
  "surface.extraction.title": "Vorschläge prüfen",
  "surface.workshops.title": "Workshops",
  "surface.workshopRunner.title": "Workshop",
  "surface.kompass.title": "Kompass",
  "surface.werte.title": "Meine Werte",
  "surface.profil.title": "Profil",
  "surface.lebenslauf.title": "Lebenslauf",
  "surface.belege.title": "Belege",
  "surface.nachweise.title": "Nachweise",
  "surface.suche.title": "Suche",
  "surface.opportunity.title": "Stelle",
  "surface.bewerbungen.title": "Bewerbungen",
  "surface.laeufe.title": "Läufe",
  "surface.einstellungen.title": "KI-Einstellungen",
  "surface.design.title": "Design & Theme",
  "surface.grading.title": "Bench-Bewertung",
  "surface.benchLab.title": "Bench-Labor",

  "heartbeat.title": "Herzschlag der Push-Ebene",
  "heartbeat.intro": "Startet einen skriptgesteuerten Suchlauf über die echte API und den echten Datenspeicher und stellt ihn live aus der Push-Ebene dar.",
  "heartbeat.trigger": "Herzschlag auslösen",
  "heartbeat.triggering": "Läuft …",
  "heartbeat.railTitle": "Suchläufe",
  "heartbeat.empty": "Noch keine Suchläufe. Löse einen Herzschlag aus, um die Live-Liste zu sehen.",
  "heartbeat.stage.discovering": "Entdecken",
  "heartbeat.stage.verifying": "Prüfen",
  "heartbeat.stage.matching": "Bewerten",
  "heartbeat.stage.enriching": "Anreichern",
  "heartbeat.stage.done": "Fertig",
  "heartbeat.status.running": "läuft",
  "heartbeat.status.completed": "abgeschlossen",
  "heartbeat.status.stopped": "gestoppt",
  "heartbeat.status.failed": "fehlgeschlagen",
  "heartbeat.count.discovered": "{n} entdeckt",
  "heartbeat.count.verified": "{n} geprüft",
  "heartbeat.count.matched": "{n} bewertet",
  "heartbeat.error": "Fehler: {message}",

  // ─── Onboarding ───────────────────────────────────────────────
  "onboarding.title": "Onboarding",
  "onboarding.lead": "Lade deine Unterlagen hoch. Wir erkennen darin deine Stärken — du entscheidest, was davon zählt.",
  "onboarding.kind.cv": "Lebenslauf",
  "onboarding.kind.letter": "Anschreiben",
  "onboarding.kind.reference": "Arbeitszeugnis",
  "onboarding.kind.certificate": "Zertifikat",
  "onboarding.kind.portrait": "Foto",
  "onboarding.formatHint": "Wir lesen PDF, DOCX, Markdown (.md) und Text (.txt).",
  "onboarding.upload": "Hochladen",
  "onboarding.uploading": "Wird hochgeladen …",
  "onboarding.uploadedN": { one: "{n} Datei hochgeladen", other: "{n} Dateien hochgeladen" },
  "onboarding.error": "Fehler: {message}",
  "onboarding.start": "Auswertung starten",
  "onboarding.starting": "Wird gestartet …",
  "onboarding.runLabel": "Auswertung",
  "onboarding.status.pending": "Bereit",
  "onboarding.status.extracting": "Wird ausgewertet …",
  "onboarding.status.proposed": "Vorschläge liegen vor",
  "onboarding.status.confirmed": "Bestätigt",
  "onboarding.status.failed": "Fehlgeschlagen",
  "onboarding.count.docs": { one: "{n} Dokument", other: "{n} Dokumente" },
  "onboarding.count.claims": { one: "{n} Aussage", other: "{n} Aussagen" },
  "onboarding.runEmpty": "Noch keine Auswertung gestartet.",
  "onboarding.zeroClaims": "Wir konnten keinen auswertbaren Text finden. Gescannte Dokumente oder Bilddateien haben oft keine Textebene — lade nach Möglichkeit eine PDF mit auswählbarem Text hoch.",
  "onboarding.toReview": "Vorschläge prüfen",

  // ─── Extraction review ────────────────────────────────────────
  "review.title": "Vorschläge prüfen",
  "review.lead": "Prüfe, was wir aus deinen Unterlagen gelesen haben. Nichts gilt als belegt, bevor du es bestätigt hast.",
  "review.error": "Fehler: {message}",
  "review.grade.evidenced": "belegt",
  "review.grade.selfPositioning": "Selbsteinschätzung",
  "review.grade.gap": "Lücke",
  "review.sources": { one: "{n} Beleg", other: "{n} Belege" },
  "review.confirm": "Bestätigen",
  "review.confirmed": "Bestätigt",
  "review.edit": "Bearbeiten",
  "review.save": "Speichern",
  "review.discard": "Verwerfen",
  "review.editLabel": "Aussage bearbeiten",
  "review.claims.title": "Aussagen",
  "review.claims.count": "{open} offen · {confirmed} bestätigt",
  "review.claims.empty": "Noch keine Aussagen. Starte zuerst die Auswertung im Onboarding.",
  "review.claims.emptyAfterRun": "Wir konnten keinen auswertbaren Text finden. Gescannte Dokumente oder Bilddateien haben oft keine Textebene — lade nach Möglichkeit eine PDF mit auswählbarem Text hoch.",
  "review.confirmIngest": "Prüfung abschließen",
  "review.rederive": "Erneut auswerten",
  "review.rederiving": "Wird erneut ausgewertet …",
  "review.cv.title": "Lebenslauf-Entwurf",
  "review.cv.name": "Name",
  "review.cv.stations": "Stationen",
  "review.cv.stationsN": { one: "{n} Station", other: "{n} Stationen" },
  "review.cv.skills": "Fähigkeiten",
  "review.cv.skillsN": { one: "{n} Fähigkeit", other: "{n} Fähigkeiten" },
  "review.cv.empty": "Noch kein Lebenslauf-Entwurf.",
  "review.cv.toProposal": "Lebenslauf-Vorschlag prüfen",
  // ─── Lebenslauf-Vorschlag (vorschlagen → übernehmen) ──────────
  "cvProposal.eyebrow": "Onboarding",
  "cvProposal.title": "Dein Lebenslauf aus deinen Unterlagen",
  "cvProposal.lead":
    "Wir haben deine hochgeladenen Unterlagen gelesen und daraus einen Lebenslauf entworfen. Übernommen wird nichts, bevor du es bestätigst — nimm einfach heraus, was nicht stimmt.",
  // The number here is a SELECTION inside this proposal (how many of the proposed entries you
  // are taking over) — NOT the »was wartet auf dich«-Zahl of the badge/chip. The label leads so
  // it can never be read as that count disagreeing with itself.
  "cvProposal.meta": "Zur Übernahme ausgewählt: {selected} von {total} Vorschlägen · gelesen am {date}",
  "cvProposal.empty": "Kein Vorschlag offen. Lade deine Unterlagen im Onboarding hoch und starte die Auswertung.",
  "cvProposal.emptyCta": "Zum Onboarding",
  "cvProposal.approve": "{n} Angaben übernehmen",
  "cvProposal.approveOne": "Eine Angabe übernehmen",
  "cvProposal.approving": "Wird übernommen …",
  "cvProposal.selectAll": "Alle auswählen",
  "cvProposal.selectNone": "Alle abwählen",
  "cvProposal.required": "bleibt — dein Name trägt den Lebenslauf",
  "cvProposal.source": "aus deinem {kind}",
  "cvProposal.sourceQuote": "Die Zeile, aus der wir das gelesen haben:",
  "cvProposal.derived": "vom Modell ergänzt — keine passende Zeile in deinen Unterlagen",
  "cvProposal.doc.cv": "Lebenslauf",
  "cvProposal.doc.letter": "Anschreiben",
  "cvProposal.doc.reference": "Zeugnis",
  "cvProposal.doc.certificate": "Zertifikat",
  "cvProposal.doc.other": "Dokument",
  "cvProposal.section.basics": "Zur Person",
  "cvProposal.section.work": "Stationen",
  "cvProposal.section.education": "Ausbildung",
  "cvProposal.section.projects": "Projekte",
  "cvProposal.section.skills": "Fähigkeiten",
  "cvProposal.section.languages": "Sprachen",
  "cvProposal.section.certificates": "Zertifikate",
  "cvProposal.section.awards": "Auszeichnungen",
  "cvProposal.section.publications": "Veröffentlichungen",
  "cvProposal.section.volunteer": "Ehrenamt",
  "cvProposal.section.references": "Referenzen",
  "cvProposal.section.interests": "Interessen",
  "cvProposal.done.title": "Übernommen — das ist jetzt dein Lebenslauf",
  "cvProposal.done.body": "Version {version} ist gesichert. {accepted} übernommen, {rejected} verworfen.",
  "cvProposal.done.toCv": "Zum Lebenslauf",
  "cvProposal.error": "Da ist etwas schiefgelaufen: {message}",

  "review.profile.title": "Profil-Ansatz",
  "review.profile.noThroughline": "Noch kein roter Faden erkannt.",
  "review.profile.motives": { one: "{m} Motiv", other: "{m} Motive" },
  "review.profile.values": { one: "{v} Wert", other: "{v} Werte" },
  "review.profile.empty": "Noch kein Profil-Ansatz.",
  "review.narrative.title": "Kandidatenprofil",
  "review.narrative.provenance": { one: "{n} Belegstelle", other: "{n} Belegstellen" },
  "review.narrative.authoredOn": "Erstellt am {date}",
  "review.narrative.stale": "Nicht mehr aktuell — bestätigte Belege haben sich geändert",
  "review.narrative.author": "Kandidatenprofil erstellen",
  "review.narrative.reauthor": "Neu erstellen",
  "review.narrative.authoring": "Wird erstellt …",
  "review.narrative.empty": "Bestätige Belege, dann entsteht hier dein Kandidatenprofil — ein Profil, das nach dir klingt.",

  // ─── Compass hub ──────────────────────────────────────────────
  "compass.lead": "Alles auf einen Blick: wer du bist, was du kannst und wie du es belegst.",
  "compass.thread": "Vom Kompass in die Suche, aus der Suche in deine Bewerbungen.",
  "compass.werte.title": "Meine Werte",
  "compass.werte.desc": "Deine bestätigte Werte-Pyramide und die vier Sätze, die dich verorten.",
  "compass.profil.title": "Profil",
  "compass.lebenslauf.title": "Lebenslauf",
  "compass.evidenzen.title": "Evidenzen",
  "compass.unterlagen.title": "Unterlagen",
  "werte.view.kicker": "Das Ergebnis von Workshop 1",
  "werte.view.lead": "Worauf du dich festgelegt hast: deine Werte-Pyramide und die vier Sätze, die dich verorten.",
  "werte.view.pyramidTitle": "Deine Werte-Pyramide",
  "werte.view.wheelTitle": "Dein Werte-Bild",
  "werte.view.statementsTitle": "Deine Sätze",
  "werte.view.stmtGap": "Hier ist noch nichts — das schärfst du in der Werte-Sektion.",
  "werte.view.empty": "Noch keine Werte festgelegt. Spiel das Kartenspiel in Workshop ① und bestätige deine Werte-Sektion.",
  "werte.view.toWorkshop": "Zu Workshop ①",
  "werte.view.canonLink": "Im Selbstbild-Workshop öffnen →",
  "compass.profile.desc": "Dein Kandidatenprofil und was dich antreibt.",
  "compass.profile.ready": "bereit",
  "compass.profile.pending": "noch nicht erstellt",
  "compass.cv.desc": "Dein Lebenslauf — Entwurf, Versionen, Export, Druck.",
  "compass.cv.versions": { one: "{n} Version", other: "{n} Versionen" },
  "compass.cv.draft": "Entwurf offen",
  "compass.evidence.desc": "Jede bewertete Aussage, die du belegen kannst.",
  "compass.evidence.confirmed": "{n} bestätigt",
  "compass.evidence.open": "{n} offen",
  "compass.assets.desc": "Deine Zeugnisse, Referenzen und Nachweise.",
  "compass.assets.count": { one: "{n} Nachweis", other: "{n} Nachweise" },

  // ─── Bereichs-Hubs (D2) ───────────────────────────────────────
  "compass.hub.eyebrow": "Dein Kompass",
  "compass.hub.title.empty": "Verschaff dir ein klares Bild von dir.",
  "compass.hub.title.ready": "Dein Kompass steht.",
  "compass.hub.lead.empty": "Alles, womit du später arbeitest, beginnt hier. Fang mit deinen Werten an.",
  "compass.hub.lead.ready": "Dein belastbares Selbstbild — nimm es mit in deine Suche.",
  "compass.hub.cta.werte": "Zur Werte-Sektion",
  "compass.hub.cta.search": "Weiter zur Suche",
  "compass.hub.chip.open": "offen",
  "compass.hub.chip.confirmed": "bestätigt",
  "compass.hub.chip.ready": "fertig",

  "workshops.hub.eyebrow": "Deine Werkstätten",
  "workshops.hub.title.empty": "Lern zuerst dich selbst kennen.",
  "workshops.hub.title.mid": "Mach weiter — du bist mittendrin.",
  "workshops.hub.title.done": "Deine Werte stehen.",
  "workshops.hub.lead.empty": "Fang mit dem Kartenspiel an — es macht aus einem Bauchgefühl Worte, auf denen du aufbauen kannst.",
  "workshops.hub.lead.mid": "Mach da weiter, wo du aufgehört hast.",
  "workshops.hub.lead.done": "Deine Werte stehen. Nimm sie mit in den Kompass.",
  "workshops.hub.cta.start": "Zum Kartenspiel",
  "workshops.hub.cta.resume": "Weitermachen",
  "workshops.hub.cta.compass": "Zum Kompass",
  "workshops.link.selfKnowledge": "Finde heraus, was dich wirklich antreibt.",
  "workshops.link.searchProfile": "Leg fest, welche Rollen du wirklich suchst.",
  "workshops.link.profiler": "Bestimme, was eine Stelle treffen muss.",
  "workshops.link.writingStyle": "Halt deine Stimme fest, damit Anschreiben nach dir klingen.",

  "search.hub.eyebrow": "Deine Suche",
  "search.hub.title.ready": "Finde Stellen, die zu dir passen.",
  "search.hub.title.notReady": "Schärfe zuerst dein Profil.",
  "search.hub.lead.ready": "Wir suchen, prüfen und bewerten — du entscheidest, was sich lohnt.",
  "search.hub.lead.notReady": "Bestätige deine Werte-Sektion — daraus suchen wir.",
  "search.hub.cta.sharpen": "Erst dein Profil schärfen",
  "search.hub.meta.results": { one: "{n} Treffer bisher", other: "{n} Treffer bisher" },
  "search.hub.meta.none": "Noch kein Suchlauf gestartet.",

  "applications.hub.eyebrow": "Deine Bewerbungen",
  "applications.hub.title.empty": "Noch keine Bewerbungen.",
  "applications.hub.title.active": { one: "{n} Bewerbung in Arbeit", other: "{n} Bewerbungen in Arbeit" },
  "applications.hub.lead.empty": "Finde eine passende Stelle — daraus machen wir eine Bewerbung, die nach dir klingt.",
  "applications.hub.lead.active": "Wo jede steht — vom ersten Entwurf bis zum Versand.",
  "applications.hub.cta.search": "Zur Suche",
  "applications.hub.cta.open": "Bewerbung öffnen",

  // ─── Goldener Pfad — der getypte »nächste Schritt« am Ende jeder Fläche (D4) ─
  // ─── W2 »Was ich suche« ────────────────────────────────────────────────────
  "sp.eyebrow": "Workshop 2",
  "sp.title": "Was ich suche",
  "sp.lead": "Dein Suchauftrag. Jedes Feld hier verändert, was die Suche findet.",
  "sp.loading": "Suchprofil wird geladen …",
  "sp.state.ready": "Startklar — deine Suche kann laufen.",
  "sp.state.blocked": "Noch nicht startklar: ohne Zielrolle findet die Suche nichts.",
  "sp.add": "Hinzufügen",
  "sp.remove": "Entfernen",
  "sp.proposal.lead": "Aus deinem Lebenslauf:",
  "sp.proposal.accept": "Übernehmen",
  "sp.roles.title": "Zielrollen",
  "sp.roles.hint": "Konkrete Stellentitel. Ohne mindestens einen läuft die Suche nicht.",
  "sp.roles.placeholder": "z. B. Lead Engineer GenAI",
  "sp.level.title": "Ebene",
  "sp.level.hint": "Wähle die Ebenen, auf die du wirklich zielst.",
  "sp.where.title": "Wo",
  "sp.where.hint": "Orte — und ob voll ortsunabhängig mitzählt.",
  "sp.where.placeholder": "z. B. Nürnberg",
  "sp.exclude.title": "Was gar nicht auftauchen soll",
  "sp.exclude.hint":
    "Anzeigen mit einem dieser Wörter im TITEL oder FIRMENNAMEN fliegen sofort raus. Der Anzeigentext selbst wird dabei nicht durchsucht.",
  "sp.exclude.placeholder": "z. B. Zeitarbeit",
  "sp.matters.title": "Worauf es dir ankommt",
  "sp.matters.hint": "Zwei Sätze in deinen eigenen Worten — sie steuern, wie Anzeigen bewertet werden.",
  "sp.matters.save": "Speichern",
  "sp.matters.derived": "Wir suchen ohnehin nach:",
  "sp.tuning.title": "Feineinstellung",
  "sp.tuning.hint": "Sinnvoll vorbelegt — ändere das nur, wenn du möchtest.",
  "sp.tuning.minTier": "Qualitätsschwelle",
  "sp.tuning.limit": "Wie viele Treffer",
  "sp.tuning.openWeb": "Karriereseiten von Unternehmen",
  "sp.tuning.openWeb.on": "Werden einbezogen",
  "sp.tuning.openWeb.off": "Nur Jobbörsen",
  "sp.tier.none": "Alles zeigen",
  "sp.tier.weak": "Ab »schwach«",
  "sp.tier.viable": "Ab »tragfähig«",
  "sp.tier.strong": "Nur »stark«",
  "sp.preview.title": "Das fragt die Suche tatsächlich ab",
  "sp.preview.frame":
    "Genau diese Anfragen stellt die Suche — aus deinem Profil gebaut, nicht wörtlich übernommen.",
  "sp.preview.empty": "Trag eine Zielrolle ein — dann stehen hier die Anfragen deiner Suche.",
  "sp.preview.count": "{n} Anfragen aus deinem Profil:",
  "sp.preview.more": "… und {n} weitere.",
  "sp.criteria.title": "Und daran wird ein Fund gemessen",
  "sp.criteria.frame":
    "Dein Suchprofil sagt, wonach gesucht wird — diese Gewichte, wie stark ein Fund zählt.",
  "sp.criteria.antiValues": "Deine roten Linien:",
  "sp.criteria.edit": "Entscheidungsprofil ändern",

  // ─── W2 ableiten-zuerst (W4): das Werkzeug schlägt Rollen vor, du entscheidest ─
  "sp.derive.title": "Lass uns dein Suchprofil ableiten",
  "sp.derive.lead":
    "Aus deinen Werten, deinen Aussagen und deinem Lebenslauf schlagen wir Rollen vor — auch solche, an die du selbst nicht gedacht hast. Du übernimmst, was passt.",
  "sp.derive.intent.placeholder": "Weißt du schon etwas, worauf es dir ankommt? (optional)",
  "sp.derive.cta": "Suchprofil aus meinen Werten & Unterlagen ableiten",
  "sp.derive.note": "Das liest deine Workshop-Ergebnisse und dauert einen Moment.",
  "sp.gate.title": "Zuerst Workshop 1",
  "sp.gate.lead":
    "Dein Suchprofil wird aus deinen Werten abgeleitet — spiel also zuerst das Kartenspiel und form deine Werte-Sektion.",
  "sp.gate.cta": "Zu Workshop 1",
  "sp.derive.pending": "Ich lese deine Werte, deine Aussagen und deinen Lebenslauf und schlage Rollen vor, die zu dir passen …",
  "sp.derive.again": "Neu ableiten",
  "sp.review.badge": "Vorschlag",
  "sp.review.note": "Noch ist nichts gespeichert — prüf ihn, pass ihn an, dann übernimm, was passt.",
  "sp.review.rolesTitle": "Rollen, die zu dir passen",
  "sp.review.accept": "Profil übernehmen",
  "sp.review.rederive": "Neu ableiten",
  "sp.review.notLoaded": "Dein gespeichertes Profil konnte nicht geladen werden — der Vorschlag lässt sich deshalb noch nicht übernehmen. Lade die Seite neu, der Vorschlag bleibt erhalten.",
  "sp.review.seniority": "Ebene",
  "sp.review.locations": "Orte",
  "sp.review.exclude": "Ausgeschlossen",
  "sp.saved.title": "Dein abgeleitetes Suchprofil",
  "sp.refine.open": "Passt nicht ganz? Sag uns, was fehlt",
  "sp.refine.placeholder": "z. B. eher Richtung Plattform-Leitung, weniger reine Entwicklung",
  "sp.refine.submit": "Mit deinem Hinweis neu ableiten",
  "sp.refine.submitting": "Wird abgeleitet …",
  "sp.refine.cancel": "Abbrechen",
  "sp.refine.proposalLabel": "Vorschlag",
  "sp.refine.accept": "Übernehmen",
  "sp.refine.again": "Nochmal",
  "sp.refine.discard": "Verwerfen",
  "sp.advanced.title": "Feineinstellungen anpassen",
  "sp.advanced.hint": "Du kannst auch jedes Feld direkt bearbeiten.",

  // ─── W3 »Wonach ich entscheide« ────────────────────────────────────────────
  "dp.eyebrow": "Workshop 3",
  "dp.title": "Wonach ich entscheide",
  "dp.lead": "Was zählt und wo deine roten Linien liegen. Beides sortiert deine Treffer neu.",
  "dp.loading": "Entscheidungsprofil wird geladen …",
  "dp.gate.title": "Zuerst dein Suchprofil",
  "dp.gate.lead":
    "Dein Entscheidungsprofil gewichtet, was deine Suche findet — richte also zuerst dein Suchprofil ein.",
  "dp.gate.cta": "Zu meinem Suchprofil",
  "dp.weights.title": "Was am meisten zählt",
  "dp.weights.hint": "Jeder Punkt, den du einem Aspekt gibst, fehlt einem anderen.",
  "dp.weights.more": "Mehr",
  "dp.weights.less": "Weniger",
  "dp.redlines.title": "Rote Linien",
  "dp.redlines.hint": "Eine Anzeige, die eine davon trifft, fällt ein bis zwei Stufen.",
  "dp.redlines.placeholder": "z. B. kein Platz für das Leben außerhalb",
  "dp.example.title": "Was das mit einem Treffer macht",
  "dp.example.lead": "Eine Beispielstelle, bewertet mit deinen jetzigen Gewichten:",
  "dp.example.score": "Bewertung {score} — {tier}",
  "dp.example.penalty": "Mit Treffer auf eine rote Linie: {score} — {tier}",
  "dp.tier.strong": "starke Passung",
  "dp.tier.viable": "tragfähig",
  "dp.tier.weak": "schwach",
  "dp.tier.unsuited": "unpassend",
  "dp.state.authored": "Gespeichert — deine Sortierung nutzt diese Gewichte.",
  "dp.state.default": "Noch nicht gesetzt — aktuell zählt alles gleich viel.",

  "next.eyebrow": "Nächster Schritt",
  "next.toSearchProfile": "Weiter zu »Was ich suche«",
  "next.provideMaterial": "Unterlagen bereitstellen",
  "next.toDecisionProfile": "Weiter zu »Wonach ich entscheide«",
  "next.locked.searchProfile": "Trage zuerst mindestens eine Zielrolle ein.",
  "next.toCardgame": "Zum Werte-Kartenspiel",
  "next.toInterview": "Weiter zum Werte-Interview",
  "next.toSektion": "Weiter zu deiner Werte-Sektion",
  "next.toKompass": "Weiter zu deinem Kompass",
  "next.toProfil": "Weiter zu deinem Profil",
  "next.toLebenslauf": "Weiter zu deinem Lebenslauf",
  "next.toSuche": "Weiter zu deiner Suche",
  "next.toBewerbungen": "Zu deinen Bewerbungen",
  "next.locked.cardgame": "Bestätige zuerst deine Werte-Pyramide.",
  "next.locked.interview": "Schließe zuerst das Werte-Interview ab.",
  "next.locked.sektion": "Bestätige zuerst deine Werte-Sektion.",
  "next.locked.werte": "Bestätige zuerst deine Werte-Sektion.",
  "next.locked.profile": "Erstelle zuerst dein Profil.",
  "next.locked.cv": "Lege zuerst deinen Lebenslauf an.",

  // ─── Profil ───────────────────────────────────────────────────
  "profile.lead": "Alles, was du über dich erarbeitet hast, an einem Ort: dein Warum, deine Werte, deine Richtung — und wie du wirkst.",
  "profile.empty": "Erstelle dein Profil: starte im Onboarding und mach deine Stärken mit Belegen sichtbar.",
  "profile.identity.title": "Dein roter Faden",
  "profile.narrative.title": "Kandidatenprofil",
  "profile.narrative.degraded": "Das ist eine vorläufige Fassung. Erstelle sie noch einmal, um dein ausformuliertes Profil zu bekommen.",
  "profile.facet.sources": { one: "{n} Beleg", other: "{n} Belege" },
  "profile.facets.motivations": "Motive",
  "profile.facets.drives": "Antriebe",
  "profile.facets.values": "Werte",
  "profile.facets.antiValues": "Was nicht zu dir passt",
  "profile.facets.dealBreakers": "No-Gos",
  "profile.constraints.title": "Rahmenbedingungen",
  "profile.constraints.location": "Ort",
  "profile.constraints.salary": "Gehalt",
  "profile.constraints.workModes": "Arbeitsmodell",

  // ─── Belegregister ────────────────────────────────────────────
  "ledger.lead": "Wir bewerten jede Aussage, die wir in deinen Unterlagen finden. Belegt ist sie erst, wenn du sie bestätigst.",
  "ledger.filters": "Filter",
  "ledger.filter.all": "Alle",
  "ledger.status.proposed": "Offen",
  "ledger.status.confirmed": "Bestätigt",
  "ledger.count.evidenced": "{n} belegt",
  "ledger.count.selfPositioning": { one: "{n} Selbsteinschätzung", other: "{n} Selbsteinschätzungen" },
  "ledger.count.gap": { one: "{n} Lücke", other: "{n} Lücken" },
  "ledger.count.confirmed": "{n} bestätigt",
  "ledger.empty": "Noch keine Aussagen. Starte zuerst die Auswertung im Onboarding.",
  "ledger.emptyFiltered": "Keine Aussagen für diesen Filter.",

  // ─── Lebenslauf-Werkbank ──────────────────────────────────────
  "cv.lead": "Finalisiere deinen Lebenslauf, wenn du zufrieden bist. Nur finalisierte Versionen kannst du exportieren.",
  "cv.empty": "Noch kein Lebenslauf. Starte im Onboarding.",
  "cv.current.title": "Aktuelle Version",
  "cv.draft.title": "Entwurf",
  "cv.draft.notExportable": "Entwurf — zum Export erst finalisieren.",
  "cv.fixVersion": "Version finalisieren",
  "cv.fixing": "Wird finalisiert …",
  "cv.portrait.add": "Foto hinzufügen",
  "cv.portrait.replace": "Foto ersetzen",
  "cv.entries.title": "Stationen & Belege",
  "cv.entries.claims": { one: "{n} verknüpfte Aussage", other: "{n} verknüpfte Aussagen" },
  "cv.entries.noClaims": "noch kein Beleg",
  "cv.versions.title": "Versionen",
  "cv.versions.empty": "Noch keine finalisierte Version. Finalisiere den Entwurf, um ihn zu exportieren oder zu drucken.",
  "cv.versions.label": "Version {n}",
  "cv.export.md": "Markdown",
  "cv.export.jsonld": "JSON-LD",
  "cv.print": "Drucken / PDF",
  "cv.print.action": "Drucken / als PDF sichern",
  "cv.print.back": "Zurück zum Lebenslauf",
  "cv.print.loading": "Wird geladen …",
  "cv.print.notFound": "Diese Version existiert nicht.",
  "cv.doc.eyebrow": "Kompass · Lebenslauf",
  "cv.doc.version": "Version {n} — finalisiert",
  "cv.belege.count": { one: "{n} Beleg", other: "{n} Belege" },
  "cv.belege.openN": "{n} offen",
  "cv.belege.toLedger": "Zum Belegregister",

  // ─── CV-Werkbank: Unterlagen bereitstellen → analysieren → klären, alles am Lebenslauf ──
  "werkbank.empty.lead": "Noch kein Lebenslauf. Stelle deinen bestehenden Lebenslauf und deine Unterlagen bereit — die KI liest sie ein, du entscheidest, was zählt.",
  "werkbank.provide.eyebrow": "Unterlagen bereitstellen",
  "werkbank.provide.title": "Beginne mit deinem bestehenden Lebenslauf",
  "werkbank.provide.titleMore": "Unterlagen hinzufügen",
  "werkbank.provide.lead": "Lade deinen Lebenslauf, Referenzen und Zeugnisse hoch. Die KI liest sie in dein Modell ein — jeder Eintrag bleibt deine Entscheidung: bestätigen oder verwerfen.",
  "werkbank.provide.start": "Analyse starten",
  "werkbank.material.pick": "Datei wählen",
  "werkbank.resolve.eyebrow": "Aus deinen Unterlagen gelesen",
  "werkbank.resolve.title": "Prüfe, was die KI erkannt hat",
  "werkbank.resolve.lead": "Nimm heraus, was nicht passt — der Rest wird dein Lebenslauf. Nichts wird gespeichert, bis du es übernimmst.",
  "werkbank.nachweise.eyebrow": "Deine Nachweise",
  "werkbank.nachweise.title": "Bestätige deine Belege",
  "werkbank.nachweise.lead": "Referenzen und Zeugnisse aus deinen Uploads — bestätige die, die du an Bewerbungen anhängen willst.",
  "werkbank.nachweise.uploaded": "Hochgeladenes Dokument ({kind})",
  "werkbank.nachweise.toLibrary": "Zur Nachweise-Bibliothek",
  "werkbank.needsYou.badge": "wartet auf dich",
  "werkbank.needsYou.section": "In diesem Abschnitt warten offene Punkte auf deine Bestätigung.",
  "werkbank.needsYou.count": { one: "{n} wartet auf dich", other: "{n} warten auf dich" },

  // ─── Nachweise ────────────────────────────────────────────────
  "asset.lead": "Deine Zeugnisse, Referenzen und dein Portfolio — Belege für deine Bewerbungen.",
  "asset.empty": "Noch keine Nachweise. Füge einen hinzu oder übernimm unten ein hochgeladenes Dokument.",
  "asset.kind.certificate": "Zertifikat",
  "asset.kind.workCertificate": "Arbeitszeugnis",
  "asset.kind.reference": "Referenz",
  "asset.kind.diploma": "Diplom",
  "asset.kind.portfolio": "Portfolio",
  "asset.kind.portrait": "Foto",
  "asset.kind.other": "Sonstiges",
  "asset.confirm": "Bestätigen",
  "asset.confirmed": "Bestätigt",
  "asset.edit": "Bearbeiten",
  "asset.save": "Speichern",
  "asset.remove": "Entfernen",
  "asset.editTitle": "Titel bearbeiten",
  "asset.editIssuer": "Aussteller bearbeiten",
  "asset.issuer": "Aussteller",
  "asset.add.group": "Nachweis hinzufügen",
  "asset.add.kindLabel": "Art",
  "asset.add.upload": "Nachweis hinzufügen",
  "asset.remove.confirmLabel": "Entfernen bestätigen",
  "asset.remove.confirmPrompt": "Wirklich entfernen?",
  "asset.remove.yes": "Ja, entfernen",
  "asset.remove.no": "Abbrechen",
  "asset.promote.title": "Hochgeladene Dokumente übernehmen",
  "asset.promote.action": "Als Nachweis übernehmen",
  "asset.promote.failed.label": "Nicht geladen",
  "asset.promote.failed.message":
    "Deine hochgeladenen Dokumente konnten gerade nicht geladen werden. Deine Nachweise oben sind vollständig — es fehlt nur die Liste zum Übernehmen. Lade die Seite neu, um es noch einmal zu versuchen.",
  "asset.promote.failed.reason": "Grund: {reason}",

  // ─── Workshops ────────────────────────────────────────────────
  "workshops.lead": "Finde heraus, was wirklich zu dir passt — eine kluge Frage nach der anderen. Drei Workshops bauen aufeinander auf.",
  "workshops.empty": "Keine Workshops verfügbar.",
  "workshop.gate.locked": "gesperrt",
  "workshop.gate.available": "bereit",
  "workshop.gate.inProgress": "läuft",
  "workshop.gate.completed": "abgeschlossen",
  "workshop.progress": "{answered}/{total} Schritte",
  "workshop.progress.stages": "{done}/{total}",
  "workshop.lockedBy": "Erst {name} abschließen",
  "workshop.action.start": "Starten",
  "workshop.action.resume": "Fortsetzen",
  "workshop.action.revisit": "Ansehen",
  "workshop.w1.stage.cardgame": "Kartenspiel",
  "workshop.w1.stage.interview": "Interview",
  "workshop.w1.stage.sektion": "Werte-Sektion",
  "workshop.w1.detail.pyramidDone": "Pyramide steht",
  "workshop.w1.detail.interviewDone": "abgeschlossen",
  "workshop.w1.detail.sektionDone": "steht",
  "workshop.w1.detail.round": "Runde {round} von {total}",
  "workshop.w1.detail.open": "noch offen",
  "workshop.w1.reader.lead": "Dein Selbstbild, in deinen Worten — lies es nach, klapp jede Etappe auf.",
  "workshop.runner.lead": "Beantworte nur, was dein Bild schärfer macht. Du kannst klar antworten, überspringen oder später korrigieren — alles ist gleich gut.",
  "workshop.runner.notStarted": "Dieser Workshop ist noch nicht gestartet.",
  "workshop.runner.notFound.title": "Diesen Workshop gibt es nicht",
  "workshop.runner.notFound.body": "Dieser Link führt zu keinem deiner Workshops. Geh zurück zur Übersicht und wähle einen aus.",
  "workshop.runner.notFound.back": "Zu den Workshops",
  "workshop.timeline.title": "Dein Weg",
  "workshop.runner.kicker": "Ein Interview über dich",
  "workshop.findings.title": "Was zählt",
  "workshop.findings.lede": "Was wir aus deinen Antworten ableiten. Korrigiere, was nicht stimmt — jede Korrektur macht die Treffer genauer.",
  "workshop.findings.confirmed": "Bestätigt",
  "workshop.findings.inferred": "Abgeleitet",
  "workshop.findings.threads": "Offene Fäden",
  "workshop.findings.empty": "Noch nichts — deine Antworten erscheinen hier.",
  "workshop.node.ueberlegung": "Überlegung",
  "workshop.node.skipped": "Übersprungen",
  "workshop.node.optional": "Optional",
  "workshop.node.yourAnswer": "Deine Antwort",
  "workshop.node.produced": { one: "{n} Beleg", other: "{n} Belege" },
  "workshop.node.pending": "Kommt als Nächstes",
  "workshop.card.generate": "Nächste Frage",
  "workshop.card.generating": "Denkt nach …",
  "workshop.card.best": "Am stärksten",
  "workshop.card.worst": "Am ehesten verzichtbar",
  "workshop.card.choose": "Das wähle ich",
  "workshop.card.situation": "Situation",
  "workshop.card.unsureLabel": "Unsicher?",
  "workshop.card.submit": "Weiter",
  "workshop.card.submitting": "Wird gespeichert …",
  "workshop.card.skipOptional": "Überspringen und Workshop abschließen",
  "workshop.card.veto": "Kommt nicht in Frage",
  "workshop.card.notSure": "Kommt drauf an",
  "workshop.card.narrativePlaceholder": "In deinen Worten …",
  "workshop.card.whyLabel": "Magst du kurz sagen, warum? (optional)",
  "workshop.card.whyPlaceholder": "Was das für dich bedeutet…",
  "workshop.card.unsupported": "Diese Kartenart wird hier noch nicht unterstützt.",
  "workshop.card.degraded": "Diese Frage konnte nicht geladen werden.",
  "workshop.card.degradedSkip": "Frage überspringen",
  "workshop.consolidate.ready": "Du hast alle nötigen Schritte erledigt. Schließe diesen Workshop ab, damit Suche und weitere Schritte darauf aufbauen können.",
  "workshop.consolidate.action": "Workshop abschließen",
  "workshop.consolidate.busy": "Workshop wird abgeschlossen …",
  "workshop.context.title": "Ergebnis",
  "workshop.context.themes": "Themen",
  "workshop.context.strengths": "Stärken",
  "workshop.context.gaps": "Lücken",
  "workshop.context.criteria": "Kriterien",
  "workshop.context.antiValues": "Rote Linien",
  "workshop.narrative.degraded": "Das ist eine vorläufige Fassung. Erstelle sie noch einmal, um deine ausformulierte Zusammenfassung zu bekommen.",
  "workshop.targeting.title": "Suchprofil bearbeiten",
  "workshop.targeting.hint": "Ein Eintrag pro Zeile.",
  "workshop.targeting.save": "Speichern",
  "workshop.targeting.roles": "Zielrollen",
  "workshop.targeting.queries": "Suchbegriffe",
  "workshop.targeting.seniority": "Erfahrungsstufen",
  "workshop.targeting.locations": "Orte",
  "workshop.targeting.exclusions": "Ausschlüsse",

  // ─── Suche ────────────────────────────────────────────────────
  "search.lead": "Wir suchen, prüfen und bewerten — du entscheidest, was sich lohnt.",
  "search.start": "Suche starten",
  "search.starting": "Wird gestartet …",
  "search.stop": "Suche stoppen",
  "search.stopping": "Wird gestoppt …",
  "search.config.title": "Wonach wir suchen",
  "search.config.empty": "—",
  "search.targetingHint": "Schärfe zuerst dein Suchprofil — je mehr du uns sagst, desto besser die Treffer.",
  "search.targetingHint.action": "Zum Suchprofil-Workshop",
  "search.unchanged.message":
    "Dieselbe Suche hat gerade nichts gefunden. Ändere deine Suchparameter — sonst kommt wieder dasselbe leere Ergebnis.",
  "search.unchanged.action": "Suchprofil anpassen",
  "search.rail.title": "Suchlauf",
  "search.rail.empty": "Noch kein Suchlauf. Starte eine Suche.",
  "search.run.failed.label": "Nicht abgeschlossen",
  "search.run.failed.message":
    "Dieser Suchlauf ist abgebrochen. Was er bis dahin gefunden hat, bleibt erhalten — starte eine neue Suche, um weiterzusuchen.",
  "search.run.failed.reason": "Grund: {reason}",
  "search.results.title": "Passende Stellen",
  "search.results.empty": "Noch keine Treffer. Starte eine Suche.",
  "search.results.running": "Wir suchen gerade Stellen, die zu dir passen …",
  "search.pipeline.title": "Deine Stellen",
  "search.pipeline.lead": "Sortiert nach Passung. Merk dir, was sich lohnt — blende den Rest aus.",
  "search.filter.label": "Anzeigen",
  "search.filter.all": "Alle",
  "search.filter.saved": "Gemerkt",
  "search.filter.urgent": "Läuft bald ab",
  "search.filter.empty.saved": "Noch nichts gemerkt. Was du dir merkst, sammelt sich hier.",
  "search.filter.empty.urgent": "Gerade läuft keine Frist bald ab.",
  "search.urgent.title": "Läuft bald ab",
  "search.urgent.lead": "Diese Anzeigen nehmen nur noch diese Woche Bewerbungen an.",
  "search.refill": "Weitere Stellen suchen",
  "search.refill.hint": "Liste durchgearbeitet? Starte einen weiteren Lauf.",
  "search.company.postings": { one: "{n} Stelle", other: "{n} Stellen" },
  "search.company.via": "über {platform}",
  "search.company.logo": "Logo von {name}",

  // ─── Stelle (Karte + Detail) ──────────────────────────────────
  "opportunity.tier.strong": "Starke Passung",
  "opportunity.tier.viable": "Solide Passung",
  "opportunity.tier.weak": "Schwache Passung",
  "opportunity.tier.unsuited": "Unpassend",
  "opportunity.tier.score": "{n}% Passung",
  "opportunity.stage.discovered": "gefunden",
  "opportunity.stage.verified": "geprüft",
  "opportunity.stage.briefed": "aufbereitet",
  "opportunity.stage.matched": "bewertet",
  "opportunity.jobState.open": "offen",
  "opportunity.jobState.closed": "geschlossen",
  "opportunity.jobState.unknown": "unbekannt",
  "opportunity.line.verified": "Wir haben die Stelle geprüft: {fit}. Sie ist {state}.",
  "opportunity.salary": "Gehalt",
  "opportunity.bookmark": "Merken",
  "opportunity.bookmarked": "Gemerkt",
  "opportunity.generate": "Bewerbung schreiben",
  "opportunity.generating": "Wird geschrieben …",
  "opportunity.dismiss": "Ausblenden",
  "opportunity.delete": "Entfernen",
  "opportunity.open": "Details",
  "opportunity.posting.link": "Zur Stellenanzeige",
  "opportunity.brief.title": "Kurzüberblick",
  "opportunity.brief.degraded": "Das ist eine vorläufige Zusammenfassung. Starte die Suche erneut, um die ausformulierte Fassung zu bekommen.",
  "opportunity.match.title": "Warum diese Stelle zu dir passt",
  "opportunity.match.strength": "{n}%",
  "opportunity.dimension.fit": "Fachliche Passung",
  "opportunity.dimension.social": "Kultur & Team",
  "opportunity.dimension.motivational": "Motivation",
  "opportunity.dimension.goal": "Ziele & Entwicklung",
  "opportunity.detail.notFound": "Diese Stelle gibt es nicht.",
  "opportunity.detail.back": "Zurück zur Suche",
  "opportunity.detail.eyebrow": "Suche · Stelle",
  "opportunity.detail.removed": "Du hast diese Stelle entfernt.",

  // ─── Signal-Tags (S9b + P-B) — kurze Etiketten, keine ausformulierte Prosa.
  //     Das Etikett steht auf der Karte, der `.hint` ist sein Hover-Titel und
  //     derselbe Satz auf der Detailseite.
  "opportunity.flag.notReal": "Anzeige fraglich",
  "opportunity.flag.notReal.hint": "Die Seite wirkt nicht wie eine echte Stellenanzeige — sieh sie dir an, bevor du Zeit investierst.",
  "opportunity.flag.sourceSuspicious": "Quelle unklar",
  "opportunity.flag.sourceSuspicious.hint": "Consent-Wall, Wiederveröffentlichung oder abgegriffene Anzeige — wir konnten die Originalseite nicht sauber lesen.",
  "opportunity.flag.fitMismatch": "Passt nicht",
  "opportunity.flag.fitMismatch.hint": "Die Anzeige nennt eine Bedingung, die dich ausschließt.",
  "opportunity.flag.fitBorderline": "Passung unklar",
  "opportunity.flag.fitBorderline.hint": "Manches passt zu dir, manches nicht — lies genauer nach.",
  "opportunity.flag.closed": "Nicht mehr offen",
  "opportunity.flag.closed.hint": "Auf der Seite gibt es keinen Weg mehr, sich zu bewerben.",
  "opportunity.flag.stale": "Womöglich veraltet",
  "opportunity.flag.stale.hint": "Wir haben die Seite gelesen — sie wirkt wie eine alte Anzeige. Prüf lieber, ob die Stelle noch besetzt wird.",
  "opportunity.flag.outOfScope": "Außerhalb deiner Suche",
  "opportunity.flag.outOfScope.hint": "Die Rolle passt nicht zu dem, was du suchst — sie ist trotzdem hier, damit du selbst entscheiden kannst.",
  "opportunity.flag.expired": "Frist abgelaufen",
  "opportunity.flag.expired.hint": "Die genannte Bewerbungsfrist ({date}) liegt in der Vergangenheit.",
  "opportunity.flag.deadline": { one: "Noch {n} Tag", other: "Noch {n} Tage" },
  "opportunity.flag.deadline.hint": "Bewerbungsschluss ist der {date}.",
  "opportunity.flag.antiValues": "Gegen deine roten Linien",
  "opportunity.flag.antiValues.hint": "Widerspricht dem, was du ausgeschlossen hast: {items}. Das hat diese Stelle Punkte gekostet.",
  "opportunity.flag.lowConfidence": "Prüfung unsicher",
  "opportunity.flag.lowConfidence.hint": "Wir konnten die Anzeige nicht sauber auslesen — diese Bewertung ist weniger belastbar als sonst.",
  "opportunity.tag.fit": "Fachlich passend",
  "opportunity.tag.social": "Kultur passt",
  "opportunity.tag.motivational": "Motivierend",
  "opportunity.tag.goal": "Ziele passen",
  "opportunity.tag.salary": "Gehalt {value}",
  "opportunity.flags.title": "Das solltest du vorher wissen",
  "opportunity.assessment.title": "Gesamteinschätzung",
  "opportunity.match.evidence": "Was dafür spricht",
  "opportunity.match.gap": "Noch offen",
  "opportunity.match.gapMark": "Lücke",

  // ─── Unternehmen ──────────────────────────────────────────────
  "org.summary.title": "Über das Unternehmen",
  "org.summary.empty": "Noch keine Infos zum Unternehmen.",
  "org.evidence": { one: "{n} Hinweis", other: "{n} Hinweise" },
  "org.employer.label": "Arbeitgeber",
  "org.signals.empty": "Noch keine belegbaren Hinweise zum Arbeitgeber.",
  "org.source.kununu": "Kununu",
  "org.source.trustpilot": "Trustpilot",
  "org.source.company": "Website",
  "org.source.news": "Presse",
  "org.source.posting": "Stellenanzeige",

  // ─── Profil-Korrektur (Bearbeiten) ────────────────────────────
  "profile.edit": "Bearbeiten",
  "profile.edit.save": "Speichern",
  "profile.edit.cancel": "Abbrechen",
  "profile.identity.throughlineLabel": "Roter Faden",
  "profile.identity.selfDescriptionLabel": "Selbstbeschreibung",
  "profile.voice.title": "Deine Stimme",
  "profile.voice.samples": "Schreibproben (eine pro Zeile)",
  "profile.voice.empty": "Noch keine Schreibproben.",

  // ─── W4 »Schreibstil« — das Schreibstil-Asset (P-A) ─────────────────────────
  "writing.block.version": "Fassung {version} · erstellt am {date}",
  "writing.block.traits": "Wie du schreibst",
  "writing.block.guidance": "Woran man dich erkennt",
  "writing.block.avoid": "Was nicht nach dir klingt",
  "writing.block.sample": "In deinen eigenen Worten",
  "writing.block.empty": "Noch kein Schreibstil hinterlegt — ohne ihn klingen deine Texte generisch.",
  "writing.block.emptyCta": "Schreibstil entwickeln",
  "writing.trait.basis": "aus {rounds}",
  "writing.confidence.low": "noch ein erster Eindruck",
  "writing.confidence.medium": "belastbar",
  "writing.confidence.high": "gut belegt",
  "writing.round.prose": "deinem Freitext",
  "writing.round.scenarios": "deinen Stilentscheidungen",
  "writing.round.validation": "deiner Schlusskorrektur",

  // ─── W4 »Schreibstil« — der Drei-Runden-Workshop (career-writing-style) ─────
  "surface.writingStyle.title": "Dein Schreibstil",
  "writing.kicker": "Workshop ④ · Schreibstil",
  "writing.locked.lede":
    "Zuerst deine Werte — dieser Workshop erfindet Situationen aus DEINEM Stoff.",
  "writing.locked.blockedBy": "Offen ist noch: {name}",
  "writing.locked.cta": "Zu deiner Werte-Sektion",
  "writing.stage.prose": "① Deine eigenen Worte",
  "writing.stage.scenarios": "② Dein Stil im Vergleich",
  "writing.stage.validation": "③ Deine Schlusskorrektur",
  "writing.gen.bullets": "Wir sammeln Stichpunkte aus deinem eigenen Material …",
  "writing.gen.scenarios": "Wir erfinden drei Situationen für dich …",
  "writing.gen.validation": "Wir schreiben einen Text in deinem Stil …",
  "writing.gen.failed": "Das hat nicht geklappt. Nichts von dem, was du geschrieben hast, ist verloren.",
  "writing.gen.retry": "Nochmal versuchen",
  "writing.prose.groundingLabel": "Woher das kommt:",
  "writing.prose.hint": "Kein Test. Ein kurzer Absatz — etwa 60 bis 120 Wörter — reicht; es geht nur darum, wie du klingst.",
  "writing.prose.placeholder": "Schreib die Stichpunkte in deinen eigenen Worten — so, wie du es jemandem erzählen würdest.",
  "writing.prose.aria": "Dein Text zu diesen Stichpunkten",
  "writing.prose.submit": "Text übernehmen",
  "writing.prose.submitting": "Wird gespeichert …",
  "writing.scenarios.lede": "Zwei Fassungen, gleicher Inhalt. Welche klingt eher nach dir?",
  "writing.scenarios.groundingLabel": "Warum diese Situation",
  "writing.scenarios.pick": "Näher an mir",
  "writing.scenarios.degraded": "Zu dieser Situation kamen keine zwei Fassungen an — überspring sie und bestätige die anderen.",
  "writing.scenarios.fitLabel": "Wie nah ist das an dir?",
  "writing.scenarios.rewriteLabel": "Schreib es so, wie du es sagen würdest",
  "writing.scenarios.rewriteHint": "Freiwillig, und am wertvollsten: korrigier die Vermutung in deinen eigenen Worten.",
  "writing.scenarios.rewritePlaceholder": "In deinen Worten …",
  "writing.scenarios.submit": "Alle Situationen bestätigen",
  "writing.scenarios.submitting": "Wird gespeichert …",
  "writing.scenarios.progress": "{done} von {total} beantwortet",
  "writing.scenarios.answered": "{n} Situationen entschieden",
  "writing.fit.trifft": "Trifft es",
  "writing.fit.fast": "Fast",
  "writing.fit.daneben": "Daneben",
  "writing.validation.lede": "Dieser Text ist in deinem Stil geschrieben. Stimmt das so — oder änderst du ihn?",
  "writing.validation.contextLabel": "Geschrieben für:",
  "writing.validation.confirm": "Passt — Stil festhalten",
  "writing.validation.confirming": "Wird festgehalten …",
  "writing.validation.edit": "Anpassen",
  "writing.validation.editHint": "Deine Fassung gewinnt — sie wird der Maßstab, nicht der Vorschlag.",
  "writing.validation.save": "Meine Fassung übernehmen",
  "writing.validation.cancel": "Abbrechen",
  "writing.validation.answered": "Du hast deinen Stil bestätigt.",
  "writing.node.change": "ändern",
  "writing.node.changeAria": "{round} noch einmal beantworten",
  "writing.done.title": "Dein Schreibstil steht",
  "writing.done.hint": "Ab jetzt klingen deine Anschreiben und Kurzprofile nach dir.",
  "profile.workmode.remote": "Remote",
  "profile.workmode.hybrid": "Hybrid",
  "profile.workmode.onsite": "Vor Ort",

  // ─── Kompass »Profil« — das vereinte digitale Kapital ─────────
  "profile.doc.kicker": "Kompass",
  "profile.doc.warum": "Dein Warum",
  "profile.doc.warumGap": "Dein Warum schärft sich in Workshop ① — hierher gehört es.",
  "profile.doc.werte": "Deine Werte",
  "profile.doc.antrieb": "Was dich antreibt",
  "profile.doc.antriebHint": "Was dich zieht und was dich abstößt — beides zählt, wenn eine Stelle passen soll.",
  "profile.doc.richtung": "Wohin du willst",
  "profile.doc.richtung.now": "Gerade jetzt",
  "profile.doc.richtung.mid": "Mittelfristig",
  "profile.doc.richtung.long": "Langfristig",
  "profile.doc.rahmenOrigin": "Du pflegst sie hier oder im Suchprofil — es ist dieselbe Angabe:",
  "profile.doc.rahmenEmpty": "Halt fest, wo und zu welchen Bedingungen du arbeiten willst — deine Treffer werden spürbar besser.",
  "profile.doc.stimmeOrigin": "Von Hand gepflegte Schreibproben. Liegt ein Schreibstil vor, hat er Vorrang.",
  "profile.doc.schreibstil": "Dein Schreibstil",
  "profile.doc.schreibstilOrigin": "Abgeleitet aus dem Schreibstil-Workshop — dieselbe Fassung steuert deine Anschreiben.",
  "profile.doc.positionierung": "Deine Positionierung",
  "profile.doc.philosophie": "Deine Arbeitsphilosophie",
  "profile.doc.vision": "Deine Vision",
  "profile.doc.identitaet": "Deine Identität",
  "profile.doc.rahmen": "Deine Rahmenbedingungen",
  "profile.doc.stimme": "Deine Stimme",
  "profile.doc.kandidatenprofil": "Dein Kandidatenprofil",
  "profile.doc.related": "Verwandt",
  "profile.doc.suchprofil": "Dein Suchprofil →",
  "profile.doc.suchprofilHint": "Was du suchst — abgeleitet aus diesem Kompass.",
  "profile.doc.buildTitle": "Bau deinen Kompass",
  "profile.doc.buildLead": "Dein Warum, deine Werte und wie du wirkst — an einem Ort. Starte mit Workshop ①.",

  // ─── Applications (slice 5) ────────────────────────────────────────────────
  "surface.bewerbungen.lead": "Aus einer passenden Stelle wird eine Bewerbung, die nach dir klingt.",
  "applications.list.title": "Deine Bewerbungen",
  "applications.empty.ready": {
    one: "{n} gemerkte Stelle wartet — wähl sie unten aus, wir schreiben die Bewerbung.",
    other: "{n} gemerkte Stellen warten — wähl unten eine aus, wir schreiben die Bewerbung.",
  },
  "applications.new": "Neue Bewerbung",
  "applications.pick.title": "Für welche Stelle bewirbst du dich?",
  "applications.pick.placeholder": "Stelle auswählen …",
  "applications.pick.available": { one: "{n} gemerkte Stelle ohne Bewerbung", other: "{n} gemerkte Stellen ohne Bewerbung" },
  "applications.pick.none": "Merk dir zuerst eine Stelle — dann schreiben wir die Bewerbung dazu.",
  "applications.pick.allApplied": "Für jede gemerkte Stelle gibt es schon eine Bewerbung. Merk dir in der Suche eine weitere.",
  "applications.pick.toSearch": "Zur Suche",
  "applications.generate": "Bewerbung schreiben",
  "applications.generating": "Wir schreiben für dich …",
  "applications.open": "Öffnen",
  "applications.rail.title": "Deine Bewerbung entsteht",
  "applications.rail.empty": "Starte eine Bewerbung und sieh zu, wie sie entsteht.",
  "applications.stop": "Stoppen",
  "applications.status.draft": "Entwurf",
  "applications.status.in_review": "In Prüfung",
  "applications.status.changes_requested": "Änderungen nötig",
  "applications.status.approved": "Freigegeben",
  "applications.status.sent": "Versendet",
  "applications.gen.stage.preselecting": "Schwerpunkte werden gewählt",
  "applications.gen.stage.authoring": "Anschreiben wird geschrieben",
  "applications.gen.stage.judging": "Anschreiben wird geprüft",
  "applications.gen.stage.kurzprofil": "Kurzprofil wird geschrieben",
  "applications.gen.stage.tailoring": "Lebenslauf wird angepasst",
  "applications.gen.stage.emailing": "E-Mail wird geschrieben",
  "applications.gen.stage.done": "Fertig",
  "applications.gen.status.running": "Läuft",
  "applications.gen.status.completed": "Abgeschlossen",
  "applications.gen.status.stopped": "Gestoppt",
  "applications.gen.status.failed": "Fehlgeschlagen",
  "applications.gen.iterations": { one: "{n} Prüfrunde", other: "{n} Prüfrunden" },
  "applications.gen.converged": "Prüfung bestanden",
  "applications.gen.notConverged": "Braucht deinen Blick",
  "review.back": "Zurück zu den Bewerbungen",
  "review.notFound": "Diese Bewerbung gibt es (noch) nicht.",
  "review.generating": "Deine Bewerbung entsteht gerade …",
  "review.progress.label": "Fortschritt deiner Bewerbung",
  "review.progress.lead": "Das dauert ein paar Minuten — du kannst die Seite verlassen, wir schreiben weiter.",
  "review.failed.title": "Die Erstellung ist fehlgeschlagen",
  "review.failed.lead": "Beim Schreiben ist etwas schiefgegangen. Meist reicht ein zweiter Anlauf — deine Stelle und deine Belege bleiben unberührt.",
  "review.failed.detail": "Technische Meldung: {message}",
  "review.failed.retry": "Noch einmal versuchen",
  "review.failed.retrying": "Wird neu gestartet …",
  "review.letter.title": "Anschreiben",
  "review.letter.degraded": "Es konnte kein ausformulierter Entwurf erstellt werden — das ist eine schlichte Notfassung. Erstelle sie neu, um es noch einmal zu versuchen.",
  "review.kurzprofil.title": "Kurzprofil",
  "review.email.title": "Begleit-E-Mail",
  "review.gates.title": "Qualitätsprüfung",
  "review.gate.evidence": "Belege",
  "review.gate.roleFit": "Passung zur Stelle",
  "review.gate.motivation": "Motivation",
  "review.gate.clarity": "Klarheit",
  "review.gate.antiSlop": "Eigene Sprache",
  "review.gate.readerTest": "Lesbarkeit",
  "review.gate.pass": "OK",
  "review.gate.fail": "Prüfen",
  "review.annotations.title": "Prüfnotizen",
  "review.severity.blocker": "Blocker",
  "review.severity.major": "Schwerwiegend",
  "review.severity.minor": "Leicht",
  "review.severity.nit": "Kleinigkeit",
  "review.severity.positive": "Positiv",
  "review.finding.label": "Prüfnotiz zu {gate}",
  "review.finding.suggestion": "Vorschlag",
  "review.finding.apply": "Vorschlag übernehmen",
  "review.finding.applying": "Wird übernommen …",
  "review.finding.dismiss": "Verwerfen",
  "review.finding.resolved": "Erledigt",
  "review.finding.open": "Prüfnotiz anzeigen",
  "review.tailoring.title": "Angepasster Lebenslauf",
  "review.tailoring.emphasis": "hervorgehoben",
  "review.attachments.title": "Anlagen",
  "review.smartlinks.title": "Links",
  "review.edit.propose": "Änderung vorschlagen",
  "review.edit.save": "Vorschlagen",
  "review.edit.cancel": "Abbrechen",
  "review.edit.approve": "Übernehmen",
  "review.edit.reject": "Verwerfen",
  "review.edit.pending": "Vorgeschlagene Änderungen",
  "review.lifecycle.title": "Status",
  "review.lifecycle.toReview": "Zur Prüfung geben",
  "review.lifecycle.approve": "Freigeben",
  "review.lifecycle.requestChanges": "Änderungen erbitten",
  "review.lifecycle.markSent": "Als versendet markieren",
  "review.lifecycle.reopen": "Wieder öffnen",
  "review.download": "Als ZIP herunterladen",

  // ─── KI-Einstellungen (§9.1 — Modell + Parameter je Integration) ──────
  "settings.hint": "Lege je KI-Funktion Modell und Sampling-Parameter fest. Leere Felder nutzen den ausgelieferten Standard; gespeichert werden nur deine Änderungen.",
  "settings.loading": "Einstellungen werden geladen…",
  "settings.error": "Fehler: {message}",
  "settings.retry": "Erneut versuchen",
  "settings.model": "Modell",
  "settings.model.default": "Standard",
  "settings.temperature": "Temperatur",
  "settings.topP": "Top-p",
  "settings.maxTokens": "Max. Tokens",
  "settings.reasoning": "Reasoning-Aufwand",
  "settings.reasoning.none": "Standard",
  "settings.reasoning.low": "Niedrig",
  "settings.reasoning.medium": "Mittel",
  "settings.reasoning.high": "Hoch",
  "settings.param.default": "Standard",
  "settings.current": "Aktuell verwendet: {model}",
  "settings.override": "Überschrieben",
  "settings.save": "Speichern",
  "settings.reset": "Zurücksetzen",
  "settings.saved": "Gespeichert",
  "settings.saveFailed": "Speichern fehlgeschlagen",

  // ─── Der optionale Freigabe-Halt (§9.2) — aus, bis DU ihn einschaltest ────
  "settings.review.label": "Vor jedem KI-Aufruf fragen",
  "settings.review.hint": "Standardmäßig aus. Solange aktiv, hält jeder deiner eigenen KI-Aufrufe an, bevor er läuft, und zeigt dir den vollständigen Prompt, die Parameter und das Modell — du kannst alles ändern und den Aufruf dann losschicken oder abbrechen. Betrifft nur deine Aufrufe; andere warten nie.",

  "hil.title": "Ein KI-Aufruf wartet auf dich",
  "hil.origin": "{seam} · aus {action}",
  "hil.seam.unknown": "unbenannter Schritt",
  "hil.action.unknown": "unbekannte Aktion",
  "hil.binding": "Läuft auf {engine} · {model}",
  "hil.deadline": "noch {seconds}s, dann wird der Aufruf verworfen",
  "hil.system": "System-Prompt",
  "hil.user": "Nachricht",
  "hil.model": "Modell",
  "hil.model.other": "Anderes Modell (frei eingeben)…",
  "hil.model.otherPlaceholder": "z. B. sonnet",
  "hil.model.otherNote": "Nicht in der Freigabeliste. Läuft nur, wenn die Engine diesen Namen selbst kennt — ein Abo-Modell hat eigene Namen (claude versteht \u0022sonnet\u0022).",
  "hil.model.unlisted": "{model} (nicht in der Freigabeliste)",
  "hil.model.unlistedNote": "Das Modell dieses Aufrufs steht nicht in der Freigabeliste — behalten oder eines aus der Liste wählen.",
  "hil.temperature": "Temperatur",
  "hil.maxTokens": "Max. Tokens",
  "hil.param.unset": "nicht gesetzt",
  "hil.schema": "Antwortformat (nur lesen)",
  "hil.approve": "Aufruf ausführen",
  "hil.reject": "Aufruf abbrechen",
  "hil.sending": "Wird gesendet…",
  "hil.queue": "{n} weitere warten",
  "hil.error": "Fehler: {message}",

  // ─── Läufe (§8 — die KI-Lauf-Spur) ────────────────────────────────────
  "surface.laeufe.lead": "Jeder KI-Schritt, offen einsehbar — mit Modell, Engine, Tokens, Parametern, Prompt und geschätzten Kosten.",
  "runs.loading": "Läufe werden geladen…",
  "runs.empty": "Noch keine KI-Läufe. Sobald du Unterlagen auswertest, einen Workshop machst oder eine Suche startest, erscheint hier jeder KI-Aufruf — mit Prompt, Parametern, Tokens und Kosten.",
  "runs.retry": "Erneut versuchen",
  "runs.ledger.runs": "Läufe",
  "runs.ledger.calls": "KI-Aufrufe",
  "runs.ledger.tokens": "Tokens",
  "runs.ledger.credits": "Credits verbraucht",
  "runs.ledger.total": "Anbieterkosten",
  "runs.credits.hint": "Abrechnungseinheit",
  "runs.credits": "{n} Credits",
  "runs.creditsOne": "{n} Credit",
  "runs.history.title": "Verlauf",
  "runs.models.title": "Eingesetzte Modelle",
  "runs.models.avgLatency": "⌀ {ms} ms",
  "runs.calls": "{n} Aufrufe",
  "runs.callsOne": "{n} Aufruf",
  "runs.engine.claude": "Claude · Abo",
  "runs.engine.codex": "Codex · Abo",
  "runs.engine.openrouter": "OpenRouter",
  "runs.engine.synthetic": "Deterministisch",
  "runs.role.system": "System-Prompt",
  "runs.role.user": "Benutzer-Nachricht",
  "runs.role.assistant": "Assistenz-Antwort",
  "runs.activity.default": "KI-Lauf",
  "runs.activity.search": "Stellensuche",
  "runs.activity.ingest": "Unterlagen auswerten",
  "runs.activity.workshop": "Workshop auswerten",
  "runs.activity.workshopCard": "Workshop · Frage erstellen",
  "runs.activity.generate": "Bewerbungserstellung",
  "runs.label.extract": "Auswertung",
  "runs.label.verify": "Prüfung",
  "runs.label.match": "Passung",
  "runs.label.orgResearch": "Arbeitgeber",
  "runs.label.orgSummary": "Arbeitgeber-Profil",
  "runs.label.brief": "Kurzbericht",
  "runs.label.triage": "Einordnung",
  "runs.label.preselect": "Vorauswahl",
  "runs.label.author": "Verfassen",
  "runs.label.kurzprofil": "Kurzprofil",
  "runs.metric.input": "Eingabe",
  "runs.metric.output": "Antwort",
  "runs.metric.reasoning": "Reasoning",
  "runs.metric.cache": "Cache-Treffer",
  "runs.metric.latency": "Latenz",
  "runs.metric.tokens": "Tokens",
  "runs.metric.yes": "ja",
  "runs.cost.title": "Anbieterkosten (EUR, intern)",
  "runs.cost.rate": "Umrechnungskurs {rate} · Grundlage: USD",
  "runs.cost.input": "Eingabe",
  "runs.cost.output": "Antwort",
  "runs.cost.total": "Gesamt",
  "runs.cost.estimated": "Aufteilung nach Token-Anteil geschätzt (keine Preise je Token verfügbar)",
  "runs.cost.cacheNote": "Cache-Tokens werden geringer gewichtet — Cache senkt die realen Kosten.",
  "runs.cost.free": "Kostenfrei · Abo €0",
  "runs.cacheChip": "{n} Cache-Tokens",
  "runs.params.title": "Parameter",
  "runs.params.none": "Keine Parameter erfasst.",
  "runs.prompt.title": "Prompt",
  "runs.prompt.truncatedTag": "gekürzt",
  "runs.prompt.truncatedNote": "Sehr lange Prompts werden gekürzt gespeichert.",
  "runs.prompt.none": "Für diesen Schritt wurde kein Prompt aufgezeichnet.",
  "runs.prompt.copy": "Kopieren",
  "runs.prompt.copyAll": "Alles kopieren",
  "runs.prompt.copied": "Kopiert",
  "runs.prompt.copyFailed": "Kopieren fehlgeschlagen",
  "runs.completion.title": "Antwort des Modells",
  "runs.completion.subtitle": "Rohantwort",
  "runs.completion.truncatedTag": "gekürzt",
  "runs.completion.truncatedNote": "Sehr lange Antworten werden gekürzt gespeichert.",
  "runs.why": "Warum dieser Prompt",
  "runs.rerun.open": "Aufruf wiederholen",
  "runs.rerun.hint": "Führt genau diesen Prompt noch einmal aus. Die Wiederholung wird als eigener Lauf erfasst und kostet Credits; der ursprüngliche Lauf bleibt unverändert.",
  "runs.rerun.model": "Modell",
  "runs.rerun.instructions": "Zusätzliche Anweisung (optional)",
  "runs.rerun.instructionsPlaceholder": "z. B. konkreter nach Situationen fragen",
  "runs.rerun.go": "Ausführen",
  "runs.rerun.running": "Läuft…",
  "runs.rerun.result.valid": "Fertig — die Antwort passte zum erwarteten Format.",
  "runs.rerun.result.invalid": "Fertig — die Antwort passte aber nicht zum erwarteten Format.",
  "runs.rerun.result.unchecked": "Fertig — das Format wurde für diesen Schritt nicht geprüft.",
  "runs.rerun.result.failed": "Die Wiederholung ist fehlgeschlagen.",
  "runs.rerun.noSchema": "ohne gespeichertes Antwortformat wiederholt",
  "runs.reviewed.plain": "freigegeben",
  "runs.reviewed.edited": "freigegeben · bearbeitet",
  "runs.reviewed.hint": "Du hast diesen Aufruf vor dem Start gesehen und unverändert durchgelassen.",
  "runs.reviewed.editedHint": "Du hast diesen Aufruf vor dem Start bearbeitet — Prompt und Parameter hier sind deine, nicht die des Schritts.",

  // ─── Guthaben (Step 5 — Aufladen, Guthaben-Chip, Hinweis bei zu wenig Guthaben) ───
  "nav.guthaben": "Guthaben",
  "surface.credits.title": "Guthaben",
  "credits.lead": "Mit Guthaben findest du Stellen und schreibst Bewerbungen. Lade auf, wann immer du möchtest.",
  "credits.balance.label": "Dein Guthaben",
  "credits.balance.unit": { one: "{n} Credit", other: "{n} Credits" },
  "credits.balance.loading": "Guthaben wird geladen …",
  "credits.balance.error": "Dein Guthaben konnte nicht geladen werden. Bitte versuch es erneut.",
  "credits.balance.retry": "Erneut versuchen",
  "credits.pack.name": "1.000 Credits",
  "credits.pack.priceLine": "10,00 € inkl. MwSt.",
  "credits.pack.cta": "Guthaben aufladen",
  "credits.pack.starting": "Sichere Bezahlung wird geöffnet …",
  "credits.pack.secure": "Du zahlst sicher auf der Seite von Stripe — deine Kartendaten sehen wir nie.",
  "credits.unavailable.title": "Aufladen ist gerade nicht möglich",
  "credits.unavailable.body": "Der Kauf von Guthaben ist kurz nicht verfügbar. Bitte versuch es gleich noch einmal.",
  "credits.checkout.error": "Die Bezahlung konnte nicht gestartet werden. Bitte versuch es noch einmal.",
  "credits.return.success.title": "Zahlung eingegangen",
  "credits.return.success.body": "Dein Guthaben wird gutgeschrieben …",
  "credits.return.success.slow": "Das dauert gerade etwas länger — dein Guthaben wird gutgeschrieben, sobald die Zahlung bestätigt ist.",
  "credits.return.success.done": "Dein Guthaben wurde aufgeladen.",
  "credits.return.cancelled.title": "Kauf abgebrochen",
  "credits.return.cancelled.body": "Es wurde nichts bezahlt. Du kannst jederzeit aufladen.",
  "credits.notice.insufficient": "Dein Guthaben reicht dafür nicht aus.",
  "credits.notice.blocked": "Lade dein Guthaben auf, um loszulegen.",
  "credits.notice.cta": "Guthaben aufladen",
  "credits.notice.dismiss": "Schließen",
  "session.expired.message": "Deine Sitzung ist abgelaufen — melde dich neu an, dein Fortschritt bleibt erhalten.",
  "session.expired.relogin": "Neu anmelden",
  "credits.chip.label": "Credits",
  "credits.chip.aria": { one: "Guthaben: {n} Credit — aufladen", other: "Guthaben: {n} Credits — aufladen" },

  // ─── Werte-Kartenspiel (W1 redesign §3, V2) ──────────────────────────────
  "surface.valuesCardgame.title": "Werte-Kartenspiel",
  "valuecard.kicker": "Workshop 1 · Deine Werte",
  "valuecard.intro.lede":
    "Finde deine persönlichen Werte im Vergleich. Du wägst Karten gegeneinander ab, verdichtest 62 auf 8 und wählst am Ende den einen Wert, der dein Leben trägt, drei, die dich stark machen, und vier, auf die du baust.",
  "valuecard.intro.start": "Los geht's",
  "valuecard.legend": "Farbfamilien",
  "valuecard.family.blau": "Blau",
  "valuecard.family.gruen": "Grün",
  "valuecard.family.gelb": "Gelb",
  "valuecard.family.rot": "Rot",
  "valuecard.reduce.prompt": "Was bedeutet dir mehr?",
  "valuecard.reduce.vs": "oder",
  "valuecard.reduce.count": "Noch {n} Karten",
  "valuecard.reduce.empty": "Nichts mehr zu vergleichen.",
  "valuecard.refine.title": "Feinschliff",
  "valuecard.refine.hint": "Das sind deine acht. Tausch eine gegen eine beiseitegelegte Karte — oder mach weiter.",
  "valuecard.refine.budget": "Noch {n} Tausche",
  "valuecard.refine.rescue": "Stattdessen hereinholen",
  "valuecard.refine.unsureTitle": "Deine unsicheren Karten",
  "valuecard.refine.noUnsure": "Du warst nirgends unsicher — hier ist alles, was du beiseitegelegt hast.",
  "valuecard.refine.noneLeft": "Nichts mehr zum Hereinholen.",
  "valuecard.refine.done": "Weiter",
  "valuecard.lebenswert.title": "Dein Lebenswert",
  "valuecard.lebenswert.hint": "An welchem einen Wert arbeitest du dein ganzes Leben — wonach strebst du am meisten?",
  "valuecard.superkraefte.title": "Deine Superkräfte",
  "valuecard.superkraefte.hint": "Mit welchen drei Werten kannst du am meisten bewirken?",
  "valuecard.superkraefte.count": "{n} von {total} gewählt",
  "valuecard.confirm.title": "Deine Wertepyramide",
  "valuecard.confirm.hint": "Stimmt das für dich? Bestätige, um sie zu behalten — du kannst jederzeit eine neue legen.",
  "valuecard.confirm.commit": "Pyramide bestätigen",
  "valuecard.confirm.committing": "Wird gespeichert…",
  "valuecard.tier.lebenswert": "Lebenswert",
  "valuecard.tier.superkraefte": "Superkräfte",
  "valuecard.tier.basiswerte": "Basiswerte",
  "valuecard.action.next": "Weiter",
  "valuecard.action.back": "Zurück",
  "valuecard.done.title": "Deine Wertepyramide",
  "valuecard.done.hint": "Das ist die Pyramide, die du gelegt hast. Du kannst jederzeit eine neue legen — die alte bleibt gültige Historie.",
  "valuecard.done.restart": "Neue Pyramide legen",
  "valuecard.done.toInterview": "Weiter zum Interview",
  "valuecard.steps.label": "Spielablauf",
  "valuecard.step.reduce": "Reduzieren",
  "valuecard.step.refine": "Feinschliff",
  "valuecard.step.lebenswert": "Lebenswert",
  "valuecard.step.superkraefte": "Superkräfte",
  "valuecard.step.confirm": "Bestätigen",
  "valuecard.step.status": "Schritt {n} von {total}: {label}",
  "valuecard.guide.reduce": "Tippe den Wert, der dir mehr bedeutet.",
  "valuecard.guide.refine": "Tausch eine deiner acht gegen eine beiseitegelegte Karte — oder behalte sie. Danach: dein einer Lebenswert.",
  "valuecard.guide.lebenswert": "Wähl den einen Wert, nach dem du am meisten strebst. Danach: drei Superkräfte.",
  "valuecard.guide.superkraefte": "Wähl die drei Werte, mit denen du am meisten bewirkst. Danach: bestätigen.",
  "valuecard.guide.confirm": "Prüf deine Pyramide und bestätige — du kannst jederzeit eine neue legen.",
  "valuecard.reduce.round": "Runde {n}",
  "valuecard.reduce.pairsLeft": "noch {n} Vergleiche in dieser Runde",
  "valuecard.reduce.unsure": "Unsicher",
  "valuecard.reduce.unsureOn": "Unsicher-Modus: wähl die Karte, die knapp gewinnt — die andere wird zum späteren Retten aufgehoben.",
  "valuecard.reduce.choose": "»{name}« wählen",
  "valuecard.reduce.firstHint": "So geht's: Tippe den Wert, der dir mehr bedeutet. »Unsicher« behält ihn, legt den anderen aber beiseite.",
  "valuecard.reduce.delayedHint": "Lieber ein Leben voller {x} und ohne {y} — oder ein Leben voller {y} und ohne {x}?",
  "valuecard.reduce.delayedHintMit": "Lieber ein Leben voll mit {x} und ohne {y} — oder ein Leben voll mit {y} und ohne {x}?",
  "valuecard.kbd.reduce": "Tastatur: ← → oder 1 / 2 wählen · U für unsicher",
  "valuecard.kbd.pick": "Tastatur: Pfeiltasten bewegen · Enter wählt",
  "valuecard.progress.label": "Fortschritt der Reduktion",

  // ─── Werte-Interview (W1 redesign §4–§5, V3/V4) ───
  "surface.valuesInterview.title": "Deine Werte, in der Tiefe",
  "interview.kicker": "Workshop 1 · Deine Werte",
  "interview.intro.needPyramid": "Lege zuerst im Kartenspiel deine Wertepyramide. Das Interview baut darauf auf.",
  "interview.intro.toCardgame": "Zum Kartenspiel",
  "interview.intro.lede":
    "Jetzt gehen wir in die Tiefe. Ein paar gezielte Fragen zu deinen eigenen Werten machen aus der Pyramide ein Bild, mit dem du wirklich arbeiten kannst — nachvollziehbar an einem anerkannten Wertemodell gespiegelt, ehrlich und mit klarer Sicherheit.",
  "interview.intro.plan": "Interview planen",
  "interview.intro.planning": "Wird geplant…",
  "interview.card.generate": "Frage anzeigen",
  "interview.card.generating": "Denkt nach…",
  "interview.card.submit": "Bestätigen",
  "interview.card.submitting": "Wird gespeichert…",
  "interview.card.best": "Am ehesten",
  "interview.card.worst": "Am wenigsten",
  "interview.card.choose": "Wähle ich",
  "interview.card.veto": "Niemals",
  "interview.card.retry": "Nochmal versuchen",
  "interview.round.preparing": "Ich stelle deine Fragen zusammen…",
  "interview.round.thinking": "Ich lese deine Antworten und überlege, was ich als Nächstes fragen will…",
  "interview.round.failed": "Diese Frage konnte nicht erstellt werden.",
  "interview.round.stillWorking": "Die Fragen werden noch erstellt — sie erscheinen hier von selbst.",
  "interview.card.openPlaceholder": "In deinen eigenen Worten…",
  "interview.card.why": "Warum diese Frage",
  "interview.card.whyLabel": "Magst du kurz sagen, warum? (optional)",
  "interview.card.whyPlaceholder": "Was das für dich bedeutet…",
  "interview.card.needsText": "Schreib zuerst einen Satz — dann kann ich ihn speichern.",
  "interview.card.saveFailed": "Das konnte nicht gespeichert werden: {message}",
  "interview.reward.eyebrow": "Dein Werte-Bild",
  "interview.block.skipped": "Aus deinen Unterlagen schon klar.",
  "interview.history.title": "Deine bisherigen Signale",
  "interview.done.title": "Das ist dein Bild",
  "interview.done.hint": "Alle Fragen sind beantwortet. Deine Signale fließen in die Zusammenfassung deiner Werte ein.",
  // PARKED mit app/lib/interview-readout.ts — auf der Interview-Fläche nicht mehr gerendert.
  "interview.readout.title": "Deine Signale",
  "interview.readout.hint": "Wie gut jede Dimension bisher gedeckt ist — ehrlich, nie zu viel behauptet.",
  "interview.readout.values": "Werte (Schwartz)",
  "interview.readout.workstyle": "Arbeitsstil (Big Five)",
  "interview.done.toSektion": "Meine Werte-Sektion verfassen",
  "interview.rail.adjusted": "Plan angepasst",
  "interview.node.change": "ändern",
  "interview.node.changeAria": "Antwort auf »{title}« ändern",
  "interview.node.recall": "Frage ansehen",

  // ─── Werte-Sektion — das abschließende Statements-Pendant (V5) ───
  "surface.werteSektion.title": "Deine Werte-Sektion",
  "sektion.kicker": "Workshop 1 · Deine Werte",
  "sektion.intro.needPyramid": "Leg zuerst deine Werte-Pyramide und beantworte das Interview — die Zusammenfassung baut darauf auf.",
  "sektion.intro.toCardgame": "Zum Kartenspiel",
  "sektion.intro.lede":
    "Aus deiner Pyramide und deinen Signalen entwerfen wir deine Werte in der Tiefe — und vier Statements über dich. Du bestätigst und bearbeitest alles, bevor es gespeichert wird.",
  "sektion.intro.synthesize": "Meine Werte-Sektion entwerfen",
  "sektion.intro.synthesizing": "Wird verfasst…",
  "sektion.resume.offer": "Dein letzter Entwurf liegt bereit — mach da weiter, wo du aufgehört hast.",
  "sektion.resume.action": "Entwurf wiederherstellen",
  "sektion.resume.waiting": "Deine Sektion wird noch erstellt — einen Moment.",
  "sektion.resynthesize": "Neu entwerfen",
  "sektion.statements.title": "Deine Statements",
  "sektion.values.title": "Deine wichtigsten Werte in der Tiefe",
  "sektion.statement.warum": "Mein Warum",
  "sektion.statement.positionierung": "Meine Positionierung",
  "sektion.statement.philosophie": "Meine Arbeitsphilosophie",
  "sektion.statement.vision": "Meine Vision",
  "sektion.value.definition": "Was das für mich bedeutet",
  "sektion.value.leitsatz": "Mein Leitsatz",
  "sektion.gap": "Noch offen — das lässt sich im Gespräch schärfen.",
  "sektion.confirm": "Meine Werte-Sektion bestätigen",
  "sektion.confirming": "Wird gespeichert…",
  "sektion.saved": "Gespeichert. Deine Werte-Sektion gehört jetzt zu deinem Kompass.",
  "sektion.edit": "Bearbeiten",
  // ─── Editorial-Dokument-Oberfläche (V5-Rebuild) ───
  "sektion.doc.values": "Deine Werte",
  "sektion.doc.valuesUnit": "Werte",
  "sektion.doc.aufEinenBlick": "Auf einen Blick",
  "sektion.doc.expandHint": "Zeig, was das für mich bedeutet",
  "sektion.review.badge": "Entwurf",
  "sektion.review.note": "Entwurf — prüfe und bearbeite alles, bevor du es bestätigst.",
  "sektion.edit.save": "Übernehmen",
  "sektion.edit.cancel": "Verwerfen",
  "sektion.edit.aria": "Diesen Abschnitt bearbeiten",
  "sektion.refine.open": "Feedback",
  "sektion.refine.aria": "KI um eine Überarbeitung bitten",
  "sektion.refine.placeholder": "Was soll anders sein? z. B. kürzer, mutiger, weniger Buzzwords…",
  "sektion.refine.submit": "Vorschlag holen",
  "sektion.refine.submitting": "Denkt nach…",
  "sektion.refine.cancel": "Abbrechen",
  "sektion.proposal.label": "Vorschlag",
  "sektion.proposal.accept": "Übernehmen",
  "sektion.proposal.discard": "Verwerfen",
  "sektion.proposal.again": "Anderes Feedback",

  // ─── Prozess-Landkarten (Admin-Konsole) ───
  "surface.prozesse.title": "Prozess-Landkarten",
  "surface.prozesse.lead":
    "Eine gezeichnete Landkarte je Anwendungsfall: was der Mensch tut, welcher Sitz läuft, welchen Prompt er schickt — und welche echten Aufrufe unter dem jeweiligen Schritt gelandet sind.",
  "processes.loading": "Prozess-Landkarten werden geladen…",
  "processes.retry": "Erneut laden",
  "processes.empty": "Es ist noch keine Prozess-Landkarte gezeichnet.",
  "processes.maps.title": "Prozess-Landkarten",
  "processes.graph.aria": "Prozessgraph: {title}",
  "processes.legend.title": "Legende",
  "processes.kind.userStep": "Schritt des Menschen",
  "processes.kind.aiSeam": "KI-Sitz",
  "processes.kind.event": "Ereignis",
  "processes.kind.inputAsset": "Eingangs-Asset",
  "processes.deterministic": "deterministisch",
  "processes.edge.flow": "Ablauf",
  "processes.edge.repair": "Reparatur (fragt sich selbst erneut)",
  "processes.edge.fallback": "Rückfall (Fehlerpfad)",
  "processes.select.hint": "Wähle einen Knoten — dann siehst du seinen Sitz, seinen Prompt und seine echten Läufe.",
  "processes.detail.route": "Route",
  "processes.seam.title": "Sitz",
  "processes.seam.none": "Kein konfigurierbarer Sitz — dieser Knoten ruft kein Modell auf.",
  "processes.seam.unresolved": "Diesen Sitz nennt die Landkarte, der Start hat ihn aber nicht aufgelöst.",
  "processes.prompt.title": "Prompt",
  "processes.prompt.system": "System",
  "processes.prompt.user": "User-Gerüst",
  "processes.prompt.none": "Dieser Knoten schickt keinen Prompt.",
  "processes.prompt.missing": "Prompt {id} ist referenziert, wurde aber nicht mitgeliefert.",
  "processes.prompt.runtimeMark": "[Grounding wird zur Laufzeit angehängt]",
  "processes.prompt.runtimeNote":
    "An der markierten Stelle hängt die Laufzeit die deutsche Grounding-Doktrin an — der echte Prompt ist länger als das, was hier steht.",
  "processes.traces.title": "Echte Läufe",
  "processes.traces.count": "{shown} von {total}",
  "processes.traces.none": "Zu diesem Knoten ist noch kein Lauf erfasst.",
  "processes.traces.noFilter":
    "Dieser Sitz schreibt keine eigene Aktivität — es gibt keinen ehrlichen Filter, der seine Läufe von denen des echten Aufrufs trennt. Woran er trotzdem zu erkennen ist, steht in der Notiz oben.",
  "processes.traces.noModelCall": "Kein Modellaufruf — zu diesem Knoten gibt es keine Läufe.",
  "processes.traces.unavailable": "Die Läufe konnten nicht geladen werden: {message}",
  "processes.trace.tokens": "Ein {input} · Aus {output}",
  "processes.trace.completion": "Antwort des Modells",
}

export type CareerCatalog = typeof catalog
export type CareerI18n = I18n<CareerCatalog>
export type CareerKey = keyof CareerCatalog

/** Build a scoped i18n instance (German-first). */
export function createCareerI18n(initialLocale = "de-DE"): CareerI18n {
  return createI18n({ catalog, locales: { "de-DE": de }, initialLocale })
}
